import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useCategoryTranslation } from '../hooks/useCategoryTranslation';
import { Trash2, ChevronLeft, ChevronRight, RefreshCw, Plus, Minus, CheckCircle2, AlertTriangle, Users, X, Square, CheckSquare, MinusSquare, Check, Wallet, Calendar, TrendingUp, Settings2, ArrowRightLeft, ChevronDown, Copy, Save } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import PeriodPicker from '../components/common/PeriodPicker';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useLivingType, useSettingsStore } from '../store/settingsStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { useNotificationStore } from '../store/notificationStore';
import { useThemeStore } from '../store/themeStore';
import { EquilibreBar, ImpactCarousel, type ImpactSlide } from '../components/dashboard';
import { useAutoSave } from '../hooks/useAutoSave';
import { useNavigationGuard } from '../hooks/useNavigationGuard';
import { getPartnerName } from '../hooks/useLivingTerms';
import ConfirmDialog, { RecurringDeleteDialog, RecurringSaveDialog } from '../components/common/ConfirmDialog';
import TreeCategoryAutocomplete from '../components/common/TreeCategoryAutocomplete';
import DateInput, { DateInputRef } from '../components/common/DateInput';
import VerticalTypeToggle from '../components/common/VerticalTypeToggle';
import AddOperationSplitButton from '../components/common/AddOperationSplitButton';
import AmountInput from '../components/common/AmountInput';
import BudgetHint from '../components/common/BudgetHint';
import OperationStatusCluster, { canConfigureRecurrence } from '../components/common/OperationStatusCluster';
import SortableHeader, { SortDirection } from '../components/common/SortableHeader';
import ColumnFilterDropdown from '../components/common/ColumnFilterDropdown';
import DateFilterContent from '../components/common/columnFilters/DateFilterContent';
import DescriptionFilterContent from '../components/common/columnFilters/DescriptionFilterContent';
import AmountFilterContent from '../components/common/columnFilters/AmountFilterContent';
import StatusFilterContent from '../components/common/columnFilters/StatusFilterContent';
import { applyColumnFilters } from '../components/common/columnFilters/filterOperationRows';
import {
  EMPTY_FILTERS,
  isDateFilterActive,
  isDescriptionFilterActive,
  isAmountFilterActive,
  isStatusFilterActive,
  type OperationColumnFilters,
  type SplitType,
} from '../components/common/columnFilters/types';
import SaveToast from '../components/common/SaveToast';
import HelpTooltip from '../components/common/HelpTooltip';
import RecurrenceConfigModal from '../components/common/RecurrenceConfigModal';
import CustomSplitConfigModal from '../components/common/CustomSplitConfigModal';
import TransferModal from '../components/common/TransferModal';
import DuplicateModal from '../components/common/DuplicateModal';
import HistoryLimitOverlay from '../components/common/HistoryLimitOverlay';
import type { Category, Settings, SharedBankAccount, RecurrenceConfig, BudgetForecastResult, EnvelopeForecastResult, EnvelopeWithProgress } from '../types';
import {
  getOperationDateBounds,
  getHistoryStartDate,
  getMonthRange,
  isInMonth,
  toISODate,
  formatMonthLabel,
  formatLongDate,
  formatShortDate,
} from '../utils/operationDates';

// Helper pour vérifier si une date est antérieure à la date de début
function isBeforeStartDate(operationDate: string, startDate: string | null): boolean {
  if (!startDate) return false;
  const opDate = new Date(operationDate);
  const start = new Date(startDate);
  // Comparer uniquement les dates (sans les heures)
  opDate.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  return opDate < start;
}

// Helper pour obtenir le dernier jour d'un mois
function getLastDayOfMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate();
}

// Helper pour formater une date en jj/mm/aaaa
function formatDateFr(day: number, month: number, year: number): string {
  return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
}

// Icône de catégorie : le champ `icon` est un NOM lucide (« zap », « home »…).
// On le convertit en composant, comme la liste déroulante des catégories.
function toPascalCase(str: string): string {
  return str.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
}
function DynamicIcon({ name, className }: { name: string; className?: string }) {
  const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[toPascalCase(name)];
  return IconComponent ? <IconComponent className={className} /> : null;
}

// Helper pour obtenir la date d'aujourd'hui formatée
function getTodayFormatted(): string {
  const today = new Date();
  return formatDateFr(today.getDate(), today.getMonth() + 1, today.getFullYear());
}

// Générer un ID temporaire unique (compatible avec tous les environnements)
let tempIdCounter = 0;
function generateTempId(): string {
  return `temp-${Date.now()}-${++tempIdCounter}-${Math.random().toString(36).substring(2, 9)}`;
}

// Vérifier si une ligne a tous les champs requis remplis (date, catégorie, sous-catégorie, montant)
function isRowComplete(row: { operationDate: string; categoryId: string; subcategoryId: string; amount: string }): boolean {
  return !!(
    row.operationDate &&
    row.categoryId &&
    row.subcategoryId &&
    row.amount &&
    parseFloat(row.amount) > 0
  );
}

const INTERNAL_TRANSFER_CATEGORY_ID = 'transfert-interne';

// Type pour une ligne d'opération
interface OperationRow {
  id?: string;
  _tempId: string; // ID temporaire unique pour la key React (évite les conflits lors de unshift)
  operationType: 'credit' | 'debit';
  categoryId: string;
  subcategoryId: string;
  customLabel: string;
  amount: string;
  operationDate: string; // Format YYYY-MM-DD
  splitType: 'proportional' | 'equal' | 'custom' | 'partner1_only' | 'partner2_only';
  customPartner1Percentage: string;
  customSplitUnit: 'percentage' | 'fixed';
  customFixedAmount: string;
  customReferencePartner: 'partner1' | 'partner2';
  isRecurring: boolean;
  recurrenceConfig: RecurrenceConfig | null; // Configuration de récurrence avancée
  isValidated: boolean;
  paidBy: 'partner1' | 'partner2' | '';
  includeInCoupleIncome: boolean;
  isInternalTransfer?: boolean;
  linkedTransferOperationId?: string;
  targetSharedBankAccountId?: string;
  envelopeId?: string;
  isNew?: boolean;
  isDirty?: boolean;
}

// ID de la sous-catégorie "Enveloppe" (doit correspondre au seed backend)
const ENVELOPE_SUBCATEGORY_ID = 'budget-enveloppe';

export default function SharedAccount() {
  const { t, i18n } = useTranslation('operations');
  const { t: tc } = useTranslation('common');
  const { translateCategoryName } = useCategoryTranslation();
  const [incomeCategories, setIncomeCategories] = useState<Category[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totals, setTotals] = useState({ credits: 0, debits: 0, balance: 0, startingBalance: 0, previousMonthsBalance: 0 });
  // Proportion de revenus de la période (aperçu du partage proportionnel en édition)
  const [incomeProportion, setIncomeProportion] = useState({ partner1Percentage: 50, partner2Percentage: 50 });
  const [settings, setSettings] = useState<Settings | null>(null);

  // Gestion des comptes partagés
  const [searchParams, setSearchParams] = useSearchParams();
  const [sharedBankAccounts, setSharedBankAccounts] = useState<SharedBankAccount[]>([]);
  const urlAccountId = searchParams.get('sharedBankAccountId');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(urlAccountId);

  // Compte sélectionné
  const selectedAccount = useMemo(
    () => sharedBankAccounts.find(a => a.id === selectedAccountId) || null,
    [sharedBankAccounts, selectedAccountId]
  );

  // Gestion du mois/année
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(now.getFullYear());

  // Vérification limite d'historique
  const planInfo = useSubscriptionStore((s) => s.planInfo);
  const isMonthRestricted = useSubscriptionStore((s) => s.isMonthRestricted);
  const isPeriodRestricted = isMonthRestricted(currentMonth, currentYear);

  // Lignes éditables
  const [rows, setRows] = useState<OperationRow[]>([]);
  const rowsRef = useRef<OperationRow[]>([]);

  // Valeurs originales des lignes (pour comparer isDirty)
  const originalRowsRef = useRef<Map<string, OperationRow>>(new Map());

  // Ref pour le champ Date de la ligne d'ajout (index 0)
  const newRowDateRef = useRef<DateInputRef>(null);

  // Ref pour stocker handleValidateNewRow (évite les stale closures dans les callbacks)
  const handleValidateNewRowRef = useRef<(index: number) => Promise<void>>();

  // État pour le dialogue de suppression récurrente
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    rowIndex: number | null;
    rowId: string | null;
    isRecurring: boolean;
  }>({ isOpen: false, rowIndex: null, rowId: null, isRecurring: false });

  // État pour le dialogue de sauvegarde récurrente
  const [recurringSaveDialog, setRecurringSaveDialog] = useState<{ isOpen: boolean }>({ isOpen: false });
  // Confirmation lorsqu'un changement de pause impacterait des opérations validées
  const [pauseConfirm, setPauseConfirm] = useState<{ count: number } | null>(null);
  const propagateToFutureRef = useRef<boolean>(true);
  // ID de la ligne en attente de sauvegarde récurrente (pour pouvoir la rétablir si annulé)
  const pendingRecurringSaveRowIdRef = useRef<string | null>(null);

  // État pour la multi-sélection
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);

  // État pour le tri des colonnes
  const [sortKey, setSortKey] = useState<string>('operationDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // État pour le filtrage par colonne
  const [filters, setFilters] = useState<OperationColumnFilters>(EMPTY_FILTERS);

  // État pour le dialogue de suppression en masse
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState<{
    isOpen: boolean;
    hasRecurring: boolean;
  }>({ isOpen: false, hasRecurring: false });

  // État pour le modal de transfert
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);

  // État pour le modal de duplication
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  // État pour le modal de configuration de récurrence
  const [recurrenceModalIndex, setRecurrenceModalIndex] = useState<number | null>(null);

  // État pour le modal de configuration de répartition personnalisée
  const [customSplitModalIndex, setCustomSplitModalIndex] = useState<number | null>(null);

  // État pour le mode édition d'une ligne existante
  const [editingRowTempId, setEditingRowTempId] = useState<string | null>(null);
  const editingRowTempIdRef = useRef<string | null>(null);

  // État pour le bouton "+" d'ajout d'opération
  const [isAddingNew, setIsAddingNew] = useState(false);
  // Type retenu pour la ligne d'ajout : il est choisi sur le bouton scindé
  // crédit/débit AVANT l'ouverture de la ligne, qui n'a donc pas de toggle.
  const [newRowType, setNewRowType] = useState<'credit' | 'debit'>('debit');
  // _tempId de la ligne d'ajout, pour la retrouver même si des duplications
  // ont été insérées au-dessus entre-temps.
  const addingRowTempIdRef = useRef<string | null>(null);

  // État pour les cartes mobiles dépliées
  const [expandedMobileIds, setExpandedMobileIds] = useState<Set<string>>(new Set());

  // État pour le jour du solde prévisionnel (par défaut: dernier jour du mois)
  const [forecastDay, setForecastDay] = useState(() => getLastDayOfMonth(now.getMonth() + 1, now.getFullYear()));

  // État pour l'impact des budgets sur le solde prévisionnel
  const [budgetImpact, setBudgetImpact] = useState<BudgetForecastResult | null>(null);
  const [envelopeImpact, setEnvelopeImpact] = useState<EnvelopeForecastResult | null>(null);
  const [availableEnvelopes, setAvailableEnvelopes] = useState<EnvelopeWithProgress[]>([]);

  // Filtrer les enveloppes selon le mois/année affiché
  const filteredEnvelopes = useMemo(() => {
    const viewMonth = new Date(currentYear, currentMonth - 1, 1);
    return availableEnvelopes.filter(env => {
      const created = new Date(env.createdAt);
      const createdMonth = new Date(created.getFullYear(), created.getMonth(), 1);

      if (env.status === 'active') {
        const target = new Date(env.targetDate);
        const targetMonth = new Date(target.getFullYear(), target.getMonth(), 1);
        return viewMonth >= createdMonth && viewMonth <= targetMonth;
      }

      if (env.status === 'spending') {
        if (env.spendingEndDate) {
          const end = new Date(env.spendingEndDate);
          const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
          return viewMonth >= createdMonth && viewMonth <= endMonth;
        }
        return viewMonth >= createdMonth;
      }

      return true;
    });
  }, [availableEnvelopes, currentMonth, currentYear]);

  const envelopeDisabledMap = useMemo((): Record<string, string> => {
    const hasActiveForAccount = filteredEnvelopes.some(e =>
      e.status === 'active' &&
      (!e.bankAccountIds || e.bankAccountIds.length === 0 || (selectedAccountId && e.bankAccountIds.includes(selectedAccountId)))
    );
    if (hasActiveForAccount) return {};
    return { [ENVELOPE_SUBCATEGORY_ID]: t('envelope.noActiveEnvelope') };
  }, [filteredEnvelopes, selectedAccountId, t]);

  // État pour les erreurs de validation de date par ligne (clé = _tempId de la ligne)
  const [dateErrors, setDateErrors] = useState<Record<string, string | null>>({});

  // Date d'un autre mois saisie sur une ligne, en attente de confirmation
  const [monthChange, setMonthChange] = useState<{
    tempId: string;
    date: string;
    isNew: boolean;
    returnFocus: HTMLElement | null;
  } | null>(null);

  // Garder une référence à jour des rows pour l'auto-save
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Garder editingRowTempIdRef synchronisé avec l'état
  useEffect(() => {
    editingRowTempIdRef.current = editingRowTempId;
  }, [editingRowTempId]);

  // Détection responsive pour le mode compact des colonnes (< 1280px = xl breakpoint)
  const [isCompact, setIsCompact] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1280);
  // Détection responsive pour le mode mobile / vue cartes (< 768px = md breakpoint)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 900);
  useEffect(() => {
    const handler = () => {
      setIsCompact(window.innerWidth < 1280);
      setIsMobile(window.innerWidth < 900);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const user = useAuthStore((state) => state.user);
  const livingType = useLivingType();
  const globalSettings = useSettingsStore((state) => state.settings);
  const themeId = useThemeStore((state) => state.themeId);
  const isMeridian = themeId === 'meridian';
  // Repli du panneau latéral (équilibre + budgets) sur mobile — fermé par défaut.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isJointOnly = globalSettings?.accountType === 'joint_only';
  const partner1Name = user?.partner1FirstName || user?.firstName || getPartnerName(1, null, livingType);
  const partner2Name = user?.partner2FirstName || getPartnerName(2, null, livingType);

  // Catégories disponibles dans le filtre Description (dépenses + revenus)
  const filterCategories = useMemo(
    () => [...expenseCategories, ...incomeCategories],
    [expenseCategories, incomeCategories],
  );
  // Libellés des modes de répartition pour le filtre Statut
  const splitTypeLabels = useMemo<Record<SplitType, string>>(() => ({
    proportional: t('splitTypes.proportional'),
    equal: t('splitTypes.equal'),
    custom: t('splitTypes.custom'),
    partner1_only: partner1Name,
    partner2_only: partner2Name,
  }), [t, partner1Name, partner2Name]);

  // Signaler les opérations enregistrées hors du mois affiché, avec un lien vers leur mois
  const notifyOtherMonths = (dates: string[], kind: 'moved' | 'created') => {
    const countByMonth = new Map<string, number>();
    for (const date of dates) {
      if (isInMonth(date, currentMonth, currentYear)) continue;
      const key = date.slice(0, 7);
      countByMonth.set(key, (countByMonth.get(key) ?? 0) + 1);
    }
    countByMonth.forEach((count, key) => {
      const [year, month] = key.split('-').map(Number);
      const label = formatMonthLabel(`${key}-01`, i18n.language);
      useNotificationStore.getState().addNotification({
        type: 'success',
        title: t(kind === 'moved' ? 'monthChange.movedTitle' : 'monthChange.createdTitle', { count }),
        message: t(count > 1 ? 'monthChange.destinationMessagePlural' : 'monthChange.destinationMessage', { month: label }),
        duration: 8000,
        action: {
          label: t('monthChange.show', { month: label }),
          onClick: () => { setCurrentMonth(month); setCurrentYear(year); },
        },
      });
    });
  };

  // Fonction de sauvegarde pour l'auto-save
  const performSave = useCallback(async (force = false) => {
    const currentRows = rowsRef.current;

    const toSave = currentRows.filter(row => {
      if (!row.isDirty) return false;
      // Les nouvelles lignes (brouillons, duplications) ne passent jamais par l'auto-save :
      // elles sont créées explicitement (bouton ✓ ou « Tout enregistrer »).
      if (row.isNew) return false;
      if (!row.categoryId || !row.amount || parseFloat(row.amount) <= 0) return false;
      return true;
    });

    if (toSave.length === 0) {
      return;
    }

    // Reload complet si :
    // - la recurrenceConfig change sur une opération déjà récurrente (nouvelles occurrences possibles)
    // - on désactive une récurrence hebdomadaire/toutes les X semaines (plusieurs occurrences dans le mois)
    const hasRecurringChange = toSave.some(row => {
      if (!row.id) return false;
      const original = originalRowsRef.current.get(row.id);
      if (!original) return false;
      if (row.isRecurring && original.isRecurring &&
        JSON.stringify(row.recurrenceConfig) !== JSON.stringify(original.recurrenceConfig)) {
        return true;
      }
      if (original.isRecurring && !row.isRecurring && original.recurrenceConfig) {
        const freq = original.recurrenceConfig.frequency;
        if (freq === 'WEEKLY' || freq === 'EVERY_N_WEEKS') return true;
      }
      return false;
    });

    // Lire et réinitialiser le flag de propagation
    const propagateToFuture = propagateToFutureRef.current;
    propagateToFutureRef.current = true;

    const operationsData = toSave.map(row => {
      const isTransfer = row.categoryId === INTERNAL_TRANSFER_CATEGORY_ID;
      const sendSplit = row.operationType === 'debit' || isTransfer;
      return {
        id: row.id,
        operationType: row.operationType,
        categoryId: row.categoryId,
        subcategoryId: isTransfer ? null : (row.subcategoryId || null),
        customLabel: row.customLabel || null,
        amount: parseFloat(row.amount),
        operationDate: row.operationDate,
        splitType: sendSplit ? row.splitType : undefined,
        customPartner1Percentage: row.splitType === 'custom' && row.customSplitUnit !== 'fixed' ? parseFloat(row.customPartner1Percentage) : undefined,
        customPartner2Percentage: row.splitType === 'custom' && row.customSplitUnit !== 'fixed' ? 100 - parseFloat(row.customPartner1Percentage) : undefined,
        customSplitUnit: row.splitType === 'custom' ? row.customSplitUnit : undefined,
        customFixedAmount: row.splitType === 'custom' && row.customSplitUnit === 'fixed' ? parseFloat(row.customFixedAmount) : undefined,
        customReferencePartner: row.splitType === 'custom' ? row.customReferencePartner : undefined,
        isRecurring: row.isRecurring,
        recurrenceConfig: row.recurrenceConfig,
        isValidated: row.isValidated,
        paidBy: row.paidBy || null,
        includeInCoupleIncome: row.operationType === 'credit' ? row.includeInCoupleIncome : undefined,
        targetSharedBankAccountId: isTransfer ? (row.subcategoryId || undefined) : undefined,
        envelopeId: row.envelopeId || null,
      };
    });

    const accountFilter = selectedAccountId ? `&sharedBankAccountId=${selectedAccountId}` : '';
    const response = await api.post('/shared-account/batch', { operations: operationsData, sharedBankAccountId: selectedAccountId || undefined, propagateToFuture, force });

    // La pause impacterait des opérations validées : demander confirmation avant d'écrire.
    // On retourne `true` pour signaler l'attente (l'appelant ne doit pas recharger les
    // données, sinon le changement de pause local serait perdu avant confirmation).
    if (response.data?.requiresConfirmation && !force) {
      setPauseConfirm({ count: response.data.validatedCount ?? 0 });
      return true;
    }

    if (response.data.success) {
      // Opérations datées d'un autre mois : elles quittent la liste affichée
      const movedRows = toSave.filter(row => !isInMonth(row.operationDate, currentMonth, currentYear));
      const movedIds = new Set(movedRows.map(row => row.id));

      if (hasRecurringChange) {
        // Recharger toutes les données pour refléter l'état réel du backend
        // (ex : isRecurring peut rester true si des occurrences passées existent)
        await fetchData();
      } else {
        // Construire un map des résultats backend par ID pour appliquer les valeurs réelles
        const resultsById = new Map<string, any>();
        if (Array.isArray(response.data.data)) {
          for (const result of response.data.data) {
            if (result?.id) resultsById.set(result.id, result);
          }
        }

        // Mise à jour légère : marquer les lignes comme non-dirty et appliquer les valeurs du backend
        movedIds.forEach(id => { if (id) originalRowsRef.current.delete(id); });
        setRows(prevRows => prevRows.filter(row => !(row.id && movedIds.has(row.id))).map(row => {
          if (row.isDirty && row.id) {
            const backendResult = resultsById.get(row.id);
            let updatedRow = { ...row, isDirty: false };

            // Appliquer isRecurring et recurrenceConfig depuis le backend
            // (le backend peut garder isRecurring=true avec une endDate si des occurrences passées existent)
            if (backendResult) {
              updatedRow.isRecurring = backendResult.isRecurring;
              updatedRow.recurrenceConfig = backendResult.isRecurring ? {
                frequency: backendResult.recurrenceFrequency || 'MONTHLY_DAY',
                day: backendResult.recurringDay,
                weekday: backendResult.recurrenceWeekday,
                weekdayOrdinal: backendResult.recurrenceWeekdayOrdinal,
                interval: backendResult.recurrenceInterval,
                endDate: backendResult.recurrenceEndDate,
                pauses: backendResult.recurrencePauses ?? [],
                totalCount: backendResult.recurrenceTotalCount,
                currentCount: backendResult.recurrenceCurrentCount,
              } : null;
            }

            originalRowsRef.current.set(row.id, { ...updatedRow });
            return updatedRow;
          }
          return row;
        }));

        const totalsResponse = await api.get(`/shared-account?month=${currentMonth}&year=${currentYear}${accountFilter}`);
        if (totalsResponse.data.success) {
          setTotals(totalsResponse.data.data.totals);
          if (totalsResponse.data.data.incomeProportion) setIncomeProportion(totalsResponse.data.data.incomeProportion);
        }
      }

      if (movedRows.length > 0) {
        setSelectedRowIds(prev => new Set([...prev].filter(id => !movedIds.has(id))));
        notifyOtherMonths(movedRows.map(row => row.operationDate), 'moved');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, currentYear, selectedAccountId]);

  // Hook d'auto-save (sauvegarde au blur)
  const { saveStatus, triggerSave } = useAutoSave({
    onSave: performSave,
    enabled: true,
  });

  // Vérifier s'il y a des modifications non sauvegardées
  const hasUnsavedChanges = useCallback(() => {
    return rowsRef.current.some(row =>
      row.isDirty &&
      !row.isNew &&
      row.categoryId &&
      row.amount &&
      parseFloat(row.amount) > 0
    );
  }, []);

  // Protection contre la perte de données lors de la navigation
  useNavigationGuard({
    hasUnsavedChanges,
    onSave: performSave,
  });

  // Charger les données
  const loadRowsFromData = (operations: any[]) => {
    const existingRows: OperationRow[] = operations.map((op: any) => ({
      id: op.id,
      _tempId: generateTempId(),
      operationType: op.operationType,
      categoryId: op.category?.id || '',
      subcategoryId: op.isInternalTransfer ? (op.targetSharedBankAccountId || '') : (op.subcategory?.id || ''),
      customLabel: op.customLabel || '',
      amount: op.amount.toString(),
      operationDate: new Date(op.operationDate).toISOString().split('T')[0],
      splitType: op.splitType || 'proportional',
      customPartner1Percentage: op.customPartner1Percentage?.toString() || '50',
      customSplitUnit: op.customSplitUnit || 'percentage',
      customFixedAmount: op.customFixedAmount?.toString() || '',
      customReferencePartner: op.customReferencePartner || 'partner1',
      isRecurring: op.isRecurring,
      recurrenceConfig: op.recurrenceConfig || null,
      isValidated: op.isValidated,
      paidBy: op.paidBy || '',
      includeInCoupleIncome: op.includeInCoupleIncome || false,
      isInternalTransfer: op.isInternalTransfer || false,
      linkedTransferOperationId: op.linkedTransferOperationId || undefined,
      targetSharedBankAccountId: op.targetSharedBankAccountId || undefined,
      envelopeId: op.envelopeId || undefined,
      isNew: false,
      isDirty: false,
    }));

    // Stocker les valeurs originales pour la comparaison isDirty
    const newOriginals = new Map<string, OperationRow>();
    existingRows.forEach(row => {
      if (row.id) newOriginals.set(row.id, { ...row });
    });
    originalRowsRef.current = newOriginals;

    setRows(existingRows);
    setIsAddingNew(false);
    addingRowTempIdRef.current = null;
    setExpandedMobileIds(new Set());
  };

  // Vérifier si une ligne a réellement changé par rapport à l'original
  const isRowChanged = (row: OperationRow): boolean => {
    if (!row.id) return true; // Nouvelle ligne = toujours dirty
    const original = originalRowsRef.current.get(row.id);
    if (!original) return true; // Pas d'original = dirty
    return (
      row.operationType !== original.operationType ||
      row.categoryId !== original.categoryId ||
      row.subcategoryId !== original.subcategoryId ||
      row.customLabel !== original.customLabel ||
      row.amount !== original.amount ||
      row.operationDate !== original.operationDate ||
      row.splitType !== original.splitType ||
      row.customPartner1Percentage !== original.customPartner1Percentage ||
      row.customSplitUnit !== original.customSplitUnit ||
      row.customFixedAmount !== original.customFixedAmount ||
      row.customReferencePartner !== original.customReferencePartner ||
      row.isRecurring !== original.isRecurring ||
      row.isValidated !== original.isValidated ||
      row.paidBy !== original.paidBy ||
      row.includeInCoupleIncome !== original.includeInCoupleIncome ||
      JSON.stringify(row.recurrenceConfig) !== JSON.stringify(original.recurrenceConfig)
    );
  };

  // Vérifie si des champs substantifs ont changé (hors isValidated, isRecurring, recurrenceConfig)
  // Ces champs ne nécessitent pas la modal de confirmation récurrente
  const hasSubstantiveChanges = (row: OperationRow): boolean => {
    if (!row.id) return true;
    const original = originalRowsRef.current.get(row.id);
    if (!original) return true;
    return (
      row.operationType !== original.operationType ||
      row.categoryId !== original.categoryId ||
      row.subcategoryId !== original.subcategoryId ||
      row.customLabel !== original.customLabel ||
      row.amount !== original.amount ||
      row.operationDate !== original.operationDate ||
      row.splitType !== original.splitType ||
      row.customPartner1Percentage !== original.customPartner1Percentage ||
      row.customSplitUnit !== original.customSplitUnit ||
      row.customFixedAmount !== original.customFixedAmount ||
      row.customReferencePartner !== original.customReferencePartner ||
      row.paidBy !== original.paidBy ||
      row.includeInCoupleIncome !== original.includeInCoupleIncome
    );
  };

  // Fetch des données
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const accountFilter = selectedAccountId ? `&sharedBankAccountId=${selectedAccountId}` : '';
      const [operationsRes, incomeCatRes, expenseCatRes, settingsRes, accountsRes] = await Promise.all([
        api.get(`/shared-account?month=${currentMonth}&year=${currentYear}${accountFilter}`),
        api.get('/categories/income?context=couple'),
        api.get('/categories/expense?context=couple'),
        api.get('/settings'),
        api.get('/shared-bank-accounts'),
      ]);

      if (operationsRes.data.success) {
        setTotals(operationsRes.data.data.totals);
        if (operationsRes.data.data.incomeProportion) setIncomeProportion(operationsRes.data.data.incomeProportion);
        loadRowsFromData(operationsRes.data.data.operations);
      }
      if (incomeCatRes.data.success) {
        setIncomeCategories(incomeCatRes.data.data);
      }
      if (expenseCatRes.data.success) {
        setExpenseCategories(expenseCatRes.data.data);
      }
      if (settingsRes.data.success) {
        setSettings(settingsRes.data.data);
      }
      if (accountsRes.data.success) {
        setSharedBankAccounts(accountsRes.data.data);
        // Auto-sélection du compte par défaut si aucun n'est sélectionné
        if (!selectedAccountId && accountsRes.data.data.length > 0) {
          const defaultAccount = accountsRes.data.data.find((acc: SharedBankAccount) => acc.isDefault);
          if (defaultAccount) {
            setSelectedAccountId(defaultAccount.id);
            setSearchParams({ sharedBankAccountId: defaultAccount.id }, { replace: true });
          }
        }
      }
    } catch {
      setRows([]);
      setIsAddingNew(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentMonth, currentYear, selectedAccountId]);

  useEffect(() => {
    if (isPeriodRestricted) {
      setIsLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, isPeriodRestricted]);

  // Synchroniser l'URL avec l'état du compte sélectionné
  useEffect(() => {
    const urlId = searchParams.get('sharedBankAccountId');
    if (urlId && urlId !== selectedAccountId) {
      setSelectedAccountId(urlId);
    }
  }, [searchParams]);

  // Rediriger vers un compte accessible si le compte sélectionné est verrouillé par le plan
  useEffect(() => {
    if (!planInfo || planInfo.limits.sharedAccounts === -1 || sharedBankAccounts.length === 0 || !selectedAccountId) return;
    const limit = planInfo.limits.sharedAccounts;
    const sorted = [...sharedBankAccounts].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
    const accessibleIds = new Set(sorted.slice(0, limit).map(a => a.id));
    if (!accessibleIds.has(selectedAccountId)) {
      const fallback = sorted[0];
      if (fallback) {
        setSelectedAccountId(fallback.id);
        setSearchParams({ sharedBankAccountId: fallback.id }, { replace: true });
      }
    }
  }, [planInfo, sharedBankAccounts, selectedAccountId, setSearchParams]);

  // Réinitialiser le jour prévisionnel quand le mois change
  useEffect(() => {
    setForecastDay(getLastDayOfMonth(currentMonth, currentYear));
  }, [currentMonth, currentYear]);

  // Charger l'impact des budgets sur le solde prévisionnel
  // Se recharge quand le mois/année/jour change, ou après une sauvegarde (via totals.debits)
  useEffect(() => {
    const fetchBudgetImpact = async () => {
      try {
        const params = new URLSearchParams();
        params.append('type', 'shared');
        params.append('month', currentMonth.toString());
        params.append('year', currentYear.toString());
        params.append('forecastDay', forecastDay.toString());

        const response = await api.get(`/budgets/forecast-impact?${params.toString()}`);
        if (response.data.success) {
          setBudgetImpact(response.data.data);
        }
      } catch {
        setBudgetImpact(null);
      }
    };

    fetchBudgetImpact();
  }, [currentMonth, currentYear, forecastDay, totals.debits]);

  // Fetch de l'impact des enveloppes sur le solde prévisionnel
  useEffect(() => {
    const fetchEnvelopeImpact = async () => {
      try {
        const params = new URLSearchParams();
        params.append('type', 'shared');
        params.append('month', String(currentMonth));
        params.append('year', String(currentYear));
        if (selectedAccountId) {
          params.append('bankAccountIds', selectedAccountId);
        }

        const response = await api.get(`/envelopes/forecast-impact?${params.toString()}`);
        if (response.data.success) {
          setEnvelopeImpact(response.data.data);
        }
      } catch {
        setEnvelopeImpact(null);
      }
    };

    fetchEnvelopeImpact();
  }, [currentMonth, currentYear, selectedAccountId, totals.debits]);

  // Fetch des enveloppes disponibles pour le sélecteur d'opérations
  useEffect(() => {
    const fetchEnvelopes = async () => {
      try {
        const response = await api.get('/envelopes?type=shared&status=active,spending');
        if (response.data.success) {
          setAvailableEnvelopes(response.data.data);
        }
      } catch {
        setAvailableEnvelopes([]);
      }
    };

    fetchEnvelopes();
  }, []);

  // Calculer le solde jusqu'à une date donnée (jour du mois en cours)
  // previousMonthsBalance = solde de fin du mois précédent (qui inclut déjà le startingBalance accumulé)
  // Pour le premier mois du budget, previousMonthsBalance = startingBalance
  // On ne doit PAS ajouter startingBalance séparément (sinon double comptage)
  const calculateBalanceUntilDay = useCallback((day: number): number => {
    const previousMonthsBalance = totals.previousMonthsBalance ?? 0;
    const startDate = selectedAccount?.startDate ?? settings?.jointAccountStartDate ?? null;

    // Ne filtrer par startDate que dans le mois ancre (même mois/année que startDate)
    // Pour les mois avant le startDate, les opérations doivent être comptées normalement
    const startDateObj = startDate ? new Date(startDate) : null;
    const isAnchorMonth = startDateObj &&
      currentMonth === (startDateObj.getMonth() + 1) &&
      currentYear === startDateObj.getFullYear();

    let credits = 0;
    let debits = 0;

    rows.forEach(row => {
      if (!row.id || !row.amount || !row.operationDate) return;

      // Ignorer les opérations avant la date de début uniquement dans le mois ancre
      if (isAnchorMonth && isBeforeStartDate(row.operationDate, startDate)) return;

      const opDate = new Date(row.operationDate);
      const opDay = opDate.getDate();

      // Ne compter que les opérations jusqu'au jour spécifié
      if (opDay <= day) {
        const amount = parseFloat(row.amount) || 0;
        if (row.operationType === 'credit') {
          credits += amount;
        } else {
          debits += amount;
        }
      }
    });

    return previousMonthsBalance + credits - debits;
  }, [rows, totals.previousMonthsBalance, selectedAccount?.startDate, settings?.jointAccountStartDate, currentMonth, currentYear]);

  // Vérifier si on est dans le mois en cours
  const isCurrentMonth = currentMonth === (now.getMonth() + 1) && currentYear === now.getFullYear();

  // Solde aujourd'hui (uniquement pertinent pour le mois en cours)
  const todayBalance = useCallback(() => {
    if (isCurrentMonth) {
      return calculateBalanceUntilDay(now.getDate());
    }
    // Si on n'est pas dans le mois en cours, retourner le solde total du mois
    return totals.balance;
  }, [isCurrentMonth, calculateBalanceUntilDay, totals.balance]);

  // Solde prévisionnel jusqu'au jour sélectionné
  const forecastBalance = useCallback(() => {
    return calculateBalanceUntilDay(forecastDay);
  }, [calculateBalanceUntilDay, forecastDay]);

  // Solde prévisionnel avec impact des budgets uniquement (enveloppes = informatif)
  const forecastBalanceWithBudget = useCallback(() => {
    const baseBalance = calculateBalanceUntilDay(forecastDay);
    let impact = 0;
    if (budgetImpact?.hasBudgets) impact += budgetImpact.totalBudgetImpact;
    return baseBalance - impact;
  }, [calculateBalanceUntilDay, forecastDay, budgetImpact]);

  const hasBudgetCharges = budgetImpact?.hasBudgets ?? false;
  const hasEnvelopeInfo = envelopeImpact?.hasEnvelopes ?? false;

  // Générer la liste des jours pour le sélecteur
  const daysInMonth = getLastDayOfMonth(currentMonth, currentYear);

  // Créer une ligne vide avec splitType basé sur le mode de calcul par défaut
  const createEmptyRow = (type: 'credit' | 'debit'): OperationRow => {
    // Déterminer le splitType par défaut selon le mode de calcul des réglages
    const defaultSplitType = globalSettings?.calculationMode === 'fixed' ? 'custom'
      : globalSettings?.calculationMode === 'equal' ? 'equal'
      : 'proportional';
    const defaultPartner1Percentage = globalSettings?.partner1Percentage?.toString() ?? '50';

    return {
      _tempId: generateTempId(),
      operationType: type,
      categoryId: '',
      subcategoryId: '',
      customLabel: '',
      amount: '',
      operationDate: '',
      splitType: defaultSplitType,
      customPartner1Percentage: defaultPartner1Percentage,
      customSplitUnit: 'percentage',
      customFixedAmount: '',
      customReferencePartner: globalSettings?.customReferencePartner ?? 'partner1',
      isRecurring: false,
      recurrenceConfig: null,
      isValidated: false,
      paidBy: '',
      includeInCoupleIncome: false,
      isNew: true,
      isDirty: false,
    };
  };

  // Navigation mois
  const goToPreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Marquer une ligne comme modifiée (en vérifiant si elle a réellement changé)
  const markRowDirty = (index: number) => {
    const newRows = [...rows];
    const dirty = isRowChanged(newRows[index]);
    if (newRows[index].isDirty !== dirty) {
      newRows[index] = { ...newRows[index], isDirty: dirty };
      setRows(newRows);
    }
  };

  // Gérer le changement d'une case à cocher sans activer le mode édition
  const handleCheckboxChange = (index: number, field: 'isValidated' | 'includeInCoupleIncome' | 'isRecurring', value: boolean) => {
    const row = rows[index];
    updateRow(index, field, value);
    // Pour les lignes existantes non en mode édition, sauvegarder immédiatement
    if (row.id && !row.isNew && editingRowTempIdRef.current !== row._tempId) {
      triggerSave();
    }
  };

  // Mise à jour d'une ligne (un ou plusieurs champs à la fois)
  const updateRowFields = (index: number, fields: Partial<OperationRow>) => {
    const currentRow = rows[index];
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], ...fields };

    // Si on change la catégorie sans fournir de sous-catégorie, réinitialiser la sous-catégorie
    if ('categoryId' in fields && !('subcategoryId' in fields)) {
      newRows[index].subcategoryId = '';
    }

    // Si on change le type d'opération, réinitialiser la catégorie et sous-catégorie
    if ('operationType' in fields) {
      newRows[index].categoryId = '';
      newRows[index].subcategoryId = '';
    }

    // Si on active isRecurring et qu'il n'y a pas de recurrenceConfig, en définir un par défaut
    if ('isRecurring' in fields && fields.isRecurring === true && !currentRow.recurrenceConfig) {
      const day = currentRow.operationDate ? new Date(currentRow.operationDate).getUTCDate() : new Date().getUTCDate();
      newRows[index].recurrenceConfig = { frequency: 'MONTHLY_DAY', day };
    }

    // Déterminer isDirty en comparant avec les valeurs originales
    newRows[index].isDirty = isRowChanged(newRows[index]);

    setRows(newRows);

    // Mettre à jour la ref immédiatement pour la sauvegarde
    rowsRef.current = newRows;
  };

  // Raccourci pour mettre à jour un seul champ
  const updateRow = (index: number, field: keyof OperationRow, value: string | boolean) => {
    updateRowFields(index, { [field]: value } as Partial<OperationRow>);
  };

  // Demander à l'utilisateur comment sauvegarder une opération récurrente
  const promptRecurringSave = () => {
    setRecurringSaveDialog({ isOpen: true });
  };

  // Ref toujours à jour pour le commit (évite les stale closures dans les événements)
  const doCommitRef = useRef<(tempId: string) => void>(() => {});
  doCommitRef.current = (tempId: string) => {
    setEditingRowTempId(null);
    editingRowTempIdRef.current = null;
    const row = rowsRef.current.find(r => r._tempId === tempId);
    if (row && !row.isNew && row.isDirty && row.categoryId && row.amount && parseFloat(row.amount) > 0) {
      if (row.isRecurring && row.id && hasSubstantiveChanges(row)) {
        pendingRecurringSaveRowIdRef.current = row.id;
        promptRecurringSave();
      } else {
        triggerSave();
      }
    }
  };

  // Entrer en mode édition pour une ligne existante
  const handleRowFocus = (row: OperationRow) => {
    if (row.isNew || !row.id) return;
    if (editingRowTempIdRef.current !== row._tempId) {
      editingRowTempIdRef.current = row._tempId;
      setEditingRowTempId(row._tempId);
    }
  };

  // Quitter le mode édition au blur de la ligne (focus parti en dehors)
  const handleRowBlur = (tempId: string, event: React.FocusEvent<HTMLElement>) => {
    if (editingRowTempIdRef.current !== tempId) return;
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (event.currentTarget.contains(relatedTarget)) return;
    // Ne pas quitter le mode édition si le focus va vers un calendrier DateInput (rendu via portail)
    if (relatedTarget?.closest('[data-dateinput-calendar]') || document.querySelector('[data-dateinput-calendar]:hover')) return;
    doCommitRef.current(tempId);
  };

  // Valider explicitement les modifications (bouton ✓)
  const commitEditMode = () => {
    const tempId = editingRowTempIdRef.current;
    if (!tempId) return;
    doCommitRef.current(tempId);
  };

  // Annuler les modifications et revenir à l'état original (bouton ✗)
  const cancelEditMode = (row: OperationRow) => {
    if (!row.id) return;
    const original = originalRowsRef.current.get(row.id);
    if (original) {
      setRows(prevRows => {
        const newRows = [...prevRows];
        const idx = newRows.findIndex(r => r._tempId === row._tempId);
        if (idx !== -1) {
          newRows[idx] = { ...original, isDirty: false };
        }
        rowsRef.current = newRows;
        return newRows;
      });
    }
    setDateErrors(prev => ({ ...prev, [row._tempId]: null }));
    setEditingRowTempId(null);
    editingRowTempIdRef.current = null;
  };

  // Valider et créer une nouvelle ligne
  const handleValidateNewRow = async (index: number) => {
    // Utiliser rowsRef pour avoir les données les plus récentes
    const currentRows = rowsRef.current;
    const row = currentRows[index];
    if (!row || !row.isNew || !isRowComplete(row)) return;

    // Préparer les données pour la sauvegarde directe
    const isTransfer = row.categoryId === INTERNAL_TRANSFER_CATEGORY_ID;
    const sendSplit = row.operationType === 'debit' || isTransfer;
    const operationData = {
      operationType: row.operationType,
      categoryId: row.categoryId,
      subcategoryId: isTransfer ? null : (row.subcategoryId || null),
      customLabel: row.customLabel || null,
      amount: parseFloat(row.amount),
      operationDate: row.operationDate,
      splitType: sendSplit ? row.splitType : undefined,
      customPartner1Percentage: row.splitType === 'custom' && row.customSplitUnit !== 'fixed' ? parseFloat(row.customPartner1Percentage) : undefined,
      customPartner2Percentage: row.splitType === 'custom' && row.customSplitUnit !== 'fixed' ? 100 - parseFloat(row.customPartner1Percentage) : undefined,
      customSplitUnit: row.splitType === 'custom' ? row.customSplitUnit : undefined,
      customFixedAmount: row.splitType === 'custom' && row.customSplitUnit === 'fixed' ? parseFloat(row.customFixedAmount) : undefined,
      customReferencePartner: row.splitType === 'custom' ? row.customReferencePartner : undefined,
      isRecurring: row.isRecurring,
      recurrenceConfig: row.recurrenceConfig,
      isValidated: row.isValidated,
      paidBy: row.paidBy || null,
      includeInCoupleIncome: row.operationType === 'credit' ? row.includeInCoupleIncome : undefined,
      sharedBankAccountId: selectedAccountId || undefined,
      targetSharedBankAccountId: isTransfer ? (row.subcategoryId || undefined) : undefined,
      envelopeId: row.envelopeId || null,
    };

    try {
      // Sauvegarder directement via l'API
      const response = await api.post('/shared-account', operationData);

      if (response.data.success) {
        const savedData = response.data.data;
        const savedId = savedData.id;
        const otherMonth = !isInMonth(row.operationDate, currentMonth, currentYear);

        // Mettre à jour la ligne avec l'ID retourné (ou la retirer si elle est
        // datée d'un autre mois) et ajouter une nouvelle ligne vide
        const newRows = [...currentRows];
        if (otherMonth) {
          newRows.splice(index, 1);
          notifyOtherMonths([row.operationDate], 'created');
        } else {
          newRows[index] = {
            ...newRows[index],
            id: savedId,
            isNew: false,
            isDirty: false,
            isInternalTransfer: savedData.isInternalTransfer || false,
            linkedTransferOperationId: savedData.linkedTransferOperationId || undefined,
            targetSharedBankAccountId: savedData.targetSharedBankAccountId || undefined,
            envelopeId: savedData.envelopeId || undefined,
          };
        }
        // Auto-ajouter une nouvelle ligne vide après enregistrement, du même
        // type que celle qui vient d'être saisie (enchaînement de dépenses)
        const emptyRow = createEmptyRow(row.operationType);
        addingRowTempIdRef.current = emptyRow._tempId;
        setNewRowType(row.operationType);
        newRows.unshift(emptyRow);
        setRows(newRows);
        setIsAddingNew(true);

        // Mettre le focus sur le champ Date de la nouvelle ligne
        setTimeout(focusNewRow, 50);

        // Mettre à jour les totaux
        const accountFilter = selectedAccountId ? `&sharedBankAccountId=${selectedAccountId}` : '';
        const totalsResponse = await api.get(`/shared-account?month=${currentMonth}&year=${currentYear}${accountFilter}`);
        if (totalsResponse.data.success) {
          setTotals(totalsResponse.data.data.totals);
          if (totalsResponse.data.data.incomeProportion) setIncomeProportion(totalsResponse.data.data.incomeProportion);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  // Mettre à jour la ref pour handleValidateNewRow
  handleValidateNewRowRef.current = handleValidateNewRow;

  // Annuler et effacer une nouvelle ligne
  const handleCancelNewRow = (index: number) => {
    const row = rows[index];
    if (!row.isNew) return;

    // Supprimer la ligne et masquer le formulaire d'ajout
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
    setIsAddingNew(false);
    addingRowTempIdRef.current = null;
  };

  // Clic sur un segment du bouton d'ajout : ouvre la ligne avec ce type, ou
  // rebascule le type de la ligne déjà ouverte (le toggle n'y est plus).
  // Ouverture d'une ligne d'ajout. `preventScroll` : le défilement natif du focus
  // plaquerait la ligne tout en haut (en-tête du tableau et bouton d'ajout hors
  // écran). C'est DateInput qui cadre la ligne — il la repère par
  // `data-scroll-anchor` — et dégage au passage la place du calendrier.
  const focusNewRow = () => {
    newRowDateRef.current?.focus({ preventScroll: true });
  };

  const handleAddOperation = (type: 'credit' | 'debit') => {
    setNewRowType(type);

    if (isAddingNew) {
      const tempId = addingRowTempIdRef.current;
      let index = tempId ? rows.findIndex(r => r._tempId === tempId) : -1;
      if (index === -1) index = rows.findIndex(r => r.isNew);
      // updateRow réinitialise catégorie et sous-catégorie, qui dépendent du type
      if (index !== -1 && rows[index].operationType !== type) {
        updateRow(index, 'operationType', type);
      }
      return;
    }

    const emptyRow = createEmptyRow(type);
    addingRowTempIdRef.current = emptyRow._tempId;
    setRows(prev => [emptyRow, ...prev]);
    setIsAddingNew(true);
    setTimeout(focusNewRow, 50);
  };

  // Callback appelé quand un champ est validé (Enter/Tab) sur une nouvelle ligne
  const handleNewRowFieldConfirm = useCallback((index: number) => {
    // Récupérer la ligne depuis rowsRef pour avoir les données les plus récentes
    const row = rowsRef.current[index];
    if (row && row.isNew && isRowComplete(row)) {
      // Tous les champs requis sont remplis, valider automatiquement
      // Utiliser la ref pour éviter les stale closures
      handleValidateNewRowRef.current?.(index);
    }
  }, []);

  // Gestion de la multi-sélection
  const selectableRows = rows.filter(r => r.id && !r.isNew);
  const allSelected = selectableRows.length > 0 && selectableRows.every(r => selectedRowIds.has(r.id!));
  const someSelected = selectableRows.some(r => selectedRowIds.has(r.id!));

  const toggleRowSelection = (rowId: string) => {
    setSelectedRowIds(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRowIds(new Set());
    } else {
      setSelectedRowIds(new Set(selectableRows.map(r => r.id!)));
    }
  };

  const clearSelection = () => {
    setSelectedRowIds(new Set());
  };

  // Actions en masse
  const selectedRows = rows.filter(r => r.id && selectedRowIds.has(r.id));
  const allSelectedValidated = selectedRows.length > 0 && selectedRows.every(r => r.isValidated);
  const allSelectedRecurring = selectedRows.length > 0 && selectedRows.every(r => r.isRecurring);
  const someSelectedRecurring = selectedRows.some(r => r.isRecurring);
  const hasInternalTransfer = selectedRows.some(r => r.isInternalTransfer || r.categoryId === INTERNAL_TRANSFER_CATEGORY_ID);

  const handleBulkToggleValidated = async () => {
    if (selectedRowIds.size === 0) return;

    // Toggle: si toutes validées → invalider, sinon → valider
    const newValidatedState = !allSelectedValidated;

    const operationsData = selectedRows.map(row => {
      const isTransfer = row.categoryId === INTERNAL_TRANSFER_CATEGORY_ID || row.isInternalTransfer;
      const sendSplit = row.operationType === 'debit' || isTransfer;
      return {
        id: row.id,
        operationType: row.operationType,
        categoryId: row.categoryId,
        subcategoryId: row.subcategoryId || null,
        customLabel: row.customLabel || null,
        amount: parseFloat(row.amount),
        operationDate: row.operationDate,
        splitType: sendSplit ? row.splitType : undefined,
        customPartner1Percentage: row.splitType === 'custom' && row.customSplitUnit !== 'fixed' ? parseFloat(row.customPartner1Percentage) : undefined,
        customPartner2Percentage: row.splitType === 'custom' && row.customSplitUnit !== 'fixed' ? 100 - parseFloat(row.customPartner1Percentage) : undefined,
        customSplitUnit: row.splitType === 'custom' ? row.customSplitUnit : undefined,
        customFixedAmount: row.splitType === 'custom' && row.customSplitUnit === 'fixed' ? parseFloat(row.customFixedAmount) : undefined,
        customReferencePartner: row.splitType === 'custom' ? row.customReferencePartner : undefined,
        isRecurring: row.isRecurring,
        recurrenceConfig: row.recurrenceConfig,
        isValidated: newValidatedState,
        paidBy: row.paidBy || null,
        includeInCoupleIncome: row.operationType === 'credit' ? row.includeInCoupleIncome : undefined,
      };
    });

    try {
      const response = await api.post('/shared-account/batch', { operations: operationsData });
      if (response.data.success) {
        clearSelection();
        fetchData();
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour en masse:', error);
    }
  };

  const handleBulkToggleRecurring = async () => {
    if (selectedRowIds.size === 0) return;

    // Capturer les données au moment de l'exécution pour éviter les problèmes de stale state
    const rowsToProcess = rows.filter(r => r.id && selectedRowIds.has(r.id));
    const allAreRecurring = rowsToProcess.length > 0 && rowsToProcess.every(r => r.isRecurring);
    const newRecurringState = !allAreRecurring;

    try {
      // Si on désactive la récurrence, supprimer les occurrences futures en parallèle
      if (!newRecurringState) {
        const recurringRowsToProcess = rowsToProcess.filter(r => r.isRecurring && r.id);
        const deletePromises = recurringRowsToProcess.map(row =>
          api.delete(`/shared-account/${row.id}/future-only`).catch(() => {
            // Ignorer les erreurs individuelles pour continuer avec les autres
          })
        );
        await Promise.all(deletePromises);
      }

      // Mettre à jour les lignes avec le nouvel état de récurrence
      const operationsData = rowsToProcess.map(row => {
        const isTransfer = row.categoryId === INTERNAL_TRANSFER_CATEGORY_ID || row.isInternalTransfer;
        const sendSplit = row.operationType === 'debit' || isTransfer;
        return {
          id: row.id,
          operationType: row.operationType,
          categoryId: row.categoryId,
          subcategoryId: row.subcategoryId || null,
          customLabel: row.customLabel || null,
          amount: parseFloat(row.amount),
          operationDate: row.operationDate,
          splitType: sendSplit ? row.splitType : undefined,
          customPartner1Percentage: row.splitType === 'custom' ? parseFloat(row.customPartner1Percentage) : undefined,
          customPartner2Percentage: row.splitType === 'custom' ? 100 - parseFloat(row.customPartner1Percentage) : undefined,
          isRecurring: newRecurringState,
          recurrenceConfig: newRecurringState ? row.recurrenceConfig : null, // Reset config si on désactive
          isValidated: row.isValidated,
          paidBy: row.paidBy || null,
          includeInCoupleIncome: row.operationType === 'credit' ? row.includeInCoupleIncome : undefined,
        };
      });

      const response = await api.post('/shared-account/batch', { operations: operationsData });
      if (response.data.success) {
        clearSelection();
        fetchData();
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour en masse:', error);
    }
  };

  // Ouvrir le dialogue de suppression en masse
  const handleBulkDeleteClick = () => {
    if (selectedRowIds.size === 0) return;
    const hasRecurring = someSelectedRecurring;
    setBulkDeleteDialog({ isOpen: true, hasRecurring });
  };

  // ===== Duplication =====
  // Construire un brouillon indépendant à partir d'une ligne source.
  // Copie toutes les données SAUF : l'identité (id), le lien avec l'originale
  // (miroir de transfert) et la récurrence (le duplicata est une opération ponctuelle).
  const buildDuplicateDraft = (source: OperationRow): OperationRow => ({
    ...source,
    _tempId: generateTempId(),
    id: undefined,
    isNew: true,
    isDirty: true,
    isRecurring: false,
    recurrenceConfig: null,
    linkedTransferOperationId: undefined,
  });

  // Dupliquer les lignes sélectionnées en N brouillons éditables (insérés en haut)
  const handleDuplicateSelection = (copies: number) => {
    const sources = rows.filter(r => r.id && selectedRowIds.has(r.id));
    if (sources.length === 0) return;
    const drafts: OperationRow[] = [];
    sources.forEach(src => {
      for (let i = 0; i < copies; i++) {
        drafts.push(buildDuplicateDraft(src));
      }
    });
    const newRows = [...drafts, ...rows];
    setRows(newRows);
    rowsRef.current = newRows;
    clearSelection();
    setDuplicateModalOpen(false);
  };

  // Construire le payload d'une opération pour l'API (création)
  const buildOperationPayload = (row: OperationRow) => {
    const isTransfer = row.categoryId === INTERNAL_TRANSFER_CATEGORY_ID;
    const sendSplit = row.operationType === 'debit' || isTransfer;
    return {
      operationType: row.operationType,
      categoryId: row.categoryId,
      subcategoryId: isTransfer ? null : (row.subcategoryId || null),
      customLabel: row.customLabel || null,
      amount: parseFloat(row.amount),
      operationDate: row.operationDate,
      splitType: sendSplit ? row.splitType : undefined,
      customPartner1Percentage: row.splitType === 'custom' && row.customSplitUnit !== 'fixed' ? parseFloat(row.customPartner1Percentage) : undefined,
      customPartner2Percentage: row.splitType === 'custom' && row.customSplitUnit !== 'fixed' ? 100 - parseFloat(row.customPartner1Percentage) : undefined,
      customSplitUnit: row.splitType === 'custom' ? row.customSplitUnit : undefined,
      customFixedAmount: row.splitType === 'custom' && row.customSplitUnit === 'fixed' ? parseFloat(row.customFixedAmount) : undefined,
      customReferencePartner: row.splitType === 'custom' ? row.customReferencePartner : undefined,
      isRecurring: row.isRecurring,
      recurrenceConfig: row.recurrenceConfig,
      isValidated: row.isValidated,
      paidBy: row.paidBy || null,
      includeInCoupleIncome: row.operationType === 'credit' ? row.includeInCoupleIncome : undefined,
      targetSharedBankAccountId: isTransfer ? (row.subcategoryId || undefined) : undefined,
      envelopeId: row.envelopeId || null,
    };
  };

  // Enregistrer tous les brouillons complets en une fois
  const handleSaveAllDrafts = async () => {
    const complete = rowsRef.current.filter(r => r.isNew && r.isDirty && isRowComplete(r));
    if (complete.length === 0) return;
    const operations = complete.map(buildOperationPayload);
    try {
      const response = await api.post('/shared-account/batch', { operations, sharedBankAccountId: selectedAccountId || undefined });
      if (response.data.success) {
        await fetchData();
        notifyOtherMonths(complete.map(r => r.operationDate), 'created');
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement des brouillons:', error);
    }
  };

  // Annuler tous les brouillons
  const handleDiscardAllDrafts = () => {
    const newRows = rows.filter(r => !(r.isNew && r.isDirty));
    setRows(newRows);
    rowsRef.current = newRows;
    setIsAddingNew(false);
    addingRowTempIdRef.current = null;
    setDateErrors({});
  };

  // Brouillons en attente (duplications + ajouts manuels non validés)
  const draftCount = rows.filter(r => r.isNew && r.isDirty).length;

  // Transférer les opérations sélectionnées vers un autre compte
  const handleBulkTransfer = async (
    destinationType: 'shared' | 'personal',
    destinationAccountId: string,
    recurringMode: 'single' | 'all_future'
  ) => {
    const ids = Array.from(selectedRowIds).filter(id => rows.find(r => r.id === id));
    if (ids.length === 0) return;
    setTransferLoading(true);
    try {
      await api.post('/shared-account/transfer', {
        operationIds: ids,
        destinationType,
        destinationAccountId,
        recurringMode,
      });
      clearSelection();
      setTransferModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Erreur lors du transfert:', error);
    } finally {
      setTransferLoading(false);
    }
  };

  // Supprimer les occurrences sélectionnées et toutes leurs occurrences futures
  const handleBulkDeleteAllFuture = async () => {
    const idsToDelete = Array.from(selectedRowIds);
    try {
      for (const id of idsToDelete) {
        const row = rows.find(r => r.id === id);
        if (row?.isRecurring) {
          await api.delete(`/shared-account/${id}/future`);
        } else {
          await api.delete(`/shared-account/${id}`);
        }
      }
      clearSelection();
      setBulkDeleteDialog({ isOpen: false, hasRecurring: false });
      fetchData();
    } catch (error) {
      console.error('Erreur lors de la suppression en masse:', error);
    }
  };

  // Ouvrir le dialogue de suppression
  const handleDeleteClick = (index: number) => {
    const row = rows[index];

    if (row.id && !row.isNew) {
      setDeleteDialog({
        isOpen: true,
        rowIndex: index,
        rowId: row.id,
        isRecurring: row.isRecurring,
      });
    } else {
      const newRows = rows.filter((_, i) => i !== index);
      setRows(newRows);
    }
  };

  // Supprimer uniquement cette occurrence
  const handleDeleteSingle = async () => {
    if (!deleteDialog.rowId) return;
    try {
      await api.delete(`/shared-account/${deleteDialog.rowId}`);
      setDeleteDialog({ isOpen: false, rowIndex: null, rowId: null, isRecurring: false });
      fetchData();
    } catch {
      // Handle error
    }
  };

  // Supprimer cette occurrence et toutes les futures
  const handleDeleteAllFuture = async () => {
    if (!deleteDialog.rowId) return;
    try {
      await api.delete(`/shared-account/${deleteDialog.rowId}/future`);
      setDeleteDialog({ isOpen: false, rowIndex: null, rowId: null, isRecurring: false });
      fetchData();
    } catch {
      // Handle error
    }
  };

  // Sauvegarder la configuration de récurrence
  const handleSaveRecurrenceConfig = async (config: RecurrenceConfig) => {
    if (recurrenceModalIndex === null) return;

    const newRows = [...rows];
    newRows[recurrenceModalIndex] = {
      ...newRows[recurrenceModalIndex],
      recurrenceConfig: config,
      isDirty: true,
    };
    setRows(newRows);
    rowsRef.current = newRows;

    // Déclencher la sauvegarde immédiate si ce n'est pas une nouvelle ligne
    // puis recharger les données pour afficher les nouvelles occurrences (ex: hebdomadaires)
    const row = newRows[recurrenceModalIndex];
    if (!row.isNew && row.categoryId && row.amount && parseFloat(row.amount) > 0) {
      const pendingConfirmation = await performSave();
      // Si une confirmation est en attente (pause impactant des opérations validées),
      // ne pas recharger : on garde le changement local jusqu'à la décision de l'utilisateur.
      if (!pendingConfirmation) await fetchData();
    }
  };

  // Sauvegarder la configuration de répartition personnalisée
  const handleSaveCustomSplitConfig = async (config: { customSplitUnit: 'percentage' | 'fixed'; customReferencePartner: 'partner1' | 'partner2'; value: string }) => {
    if (customSplitModalIndex === null) return;

    const fields: Partial<OperationRow> = {
      customSplitUnit: config.customSplitUnit,
      customReferencePartner: config.customReferencePartner,
    };

    if (config.customSplitUnit === 'fixed') {
      fields.customFixedAmount = config.value;
    } else {
      fields.customPartner1Percentage = config.value;
    }

    // Mettre à jour la ligne directement (comme handleSaveRecurrenceConfig)
    const newRows = [...rows];
    newRows[customSplitModalIndex] = {
      ...newRows[customSplitModalIndex],
      ...fields,
      isDirty: true,
    };
    setRows(newRows);
    rowsRef.current = newRows;

    // Déclencher la sauvegarde immédiate si ce n'est pas une nouvelle ligne
    const row = newRows[customSplitModalIndex];
    if (!row.isNew && row.categoryId && row.amount && parseFloat(row.amount) > 0) {
      await performSave();
    }
  };

  // Obtenir les catégories selon le type d'opération
  const getCategoriesForType = (operationType: 'credit' | 'debit') => {
    return operationType === 'credit' ? incomeCategories : expenseCategories;
  };

  // Obtenir les sous-catégories
  const getSubcategories = (categoryId: string, operationType: 'credit' | 'debit') => {
    // Si c'est un transfert interne, générer les sous-catégories dynamiquement
    if (categoryId === INTERNAL_TRANSFER_CATEGORY_ID) {
      // Exclure le compte actuellement sélectionné
      const otherAccounts = sharedBankAccounts.filter(acc => acc.id !== selectedAccountId);
      return otherAccounts.map(acc => ({
        id: acc.id,
        name: operationType === 'debit' ? t('internalTransfer.toAccount', { name: acc.name }) : t('internalTransfer.fromAccount', { name: acc.name }),
        icon: '↔️',
        isDefault: false,
      }));
    }
    const categories = getCategoriesForType(operationType);
    const category = categories.find(c => c.id === categoryId);
    return category?.subcategories || [];
  };

  // Obtenir le nom d'une catégorie par son ID
  const getCategoryName = (categoryId: string, operationType: 'credit' | 'debit'): string => {
    const categories = getCategoriesForType(operationType);
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return '';
    return translateCategoryName(cat.id, cat.name, cat.isDefault);
  };

  // Obtenir le nom d'une sous-catégorie par son ID
  const getSubcategoryName = (categoryId: string, subcategoryId: string, operationType: 'credit' | 'debit'): string => {
    const subcategories = getSubcategories(categoryId, operationType);
    const sub = subcategories.find(s => s.id === subcategoryId);
    if (!sub) return '';
    return translateCategoryName(sub.id, sub.name, sub.isDefault);
  };

  // Gestion du tri
  const handleSort = (key: string) => {
    if (sortKey === key) {
      // Toggle direction if same key
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New key, default to ascending
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Tri des lignes
  const sortedRows = useMemo(() => {
    // La première ligne (index 0) est toujours la ligne d'ajout, on ne la trie pas
    const newRows = rows.filter(row => row.isNew);
    // Appliquer les filtres de colonne aux lignes existantes (les nouvelles restent visibles)
    const existingRows = applyColumnFilters(
      rows.filter(row => !row.isNew),
      filters,
      getCategoryName,
      getSubcategoryName,
    );

    if (!sortKey || !sortDirection) {
      return [...newRows, ...existingRows];
    }

    // Pour les lignes en cours de modification (isDirty), on utilise les valeurs
    // originales afin que la position dans la liste ne change pas tant que la
    // modification n'a pas été validée.
    const getSortSource = (row: OperationRow): OperationRow => {
      if (!row.isDirty || !row.id) return row;
      return originalRowsRef.current.get(row.id) ?? row;
    };

    const sorted = [...existingRows].sort((a, b) => {
      const aSrc = getSortSource(a);
      const bSrc = getSortSource(b);
      let aValue: string | number | boolean = '';
      let bValue: string | number | boolean = '';

      switch (sortKey) {
        case 'operationType':
          aValue = aSrc.operationType;
          bValue = bSrc.operationType;
          break;
        case 'operationDate':
          aValue = aSrc.operationDate;
          bValue = bSrc.operationDate;
          break;
        case 'category':
          aValue = getCategoryName(aSrc.categoryId, aSrc.operationType).toLowerCase();
          bValue = getCategoryName(bSrc.categoryId, bSrc.operationType).toLowerCase();
          break;
        case 'subcategory':
          aValue = getSubcategoryName(aSrc.categoryId, aSrc.subcategoryId, aSrc.operationType).toLowerCase();
          bValue = getSubcategoryName(bSrc.categoryId, bSrc.subcategoryId, bSrc.operationType).toLowerCase();
          break;
        case 'customLabel':
          aValue = (aSrc.customLabel || '').toLowerCase();
          bValue = (bSrc.customLabel || '').toLowerCase();
          break;
        case 'amount':
          aValue = parseFloat(aSrc.amount) || 0;
          bValue = parseFloat(bSrc.amount) || 0;
          break;
        case 'splitType':
          aValue = aSrc.splitType;
          bValue = bSrc.splitType;
          break;
        case 'isValidated':
          aValue = aSrc.isValidated ? 1 : 0;
          bValue = bSrc.isValidated ? 1 : 0;
          break;
        case 'isRecurring':
          aValue = aSrc.isRecurring ? 1 : 0;
          bValue = bSrc.isRecurring ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return [...newRows, ...sorted];
  }, [rows, sortKey, sortDirection, incomeCategories, expenseCategories, filters]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: 'EUR' }).format(amount);

  // Calcule la part de chaque partenaire pour une opération en cours d'édition.
  // Uniquement pour proportional / equal / custom (pas partner1_only / partner2_only).
  const getSplitPreview = (row: OperationRow): { partner1: number; partner2: number } | null => {
    if (row.operationType !== 'debit') return null;
    const total = parseFloat(row.amount) || 0;
    switch (row.splitType) {
      case 'equal':
        return { partner1: total / 2, partner2: total / 2 };
      case 'proportional':
        return {
          partner1: (total * incomeProportion.partner1Percentage) / 100,
          partner2: (total * incomeProportion.partner2Percentage) / 100,
        };
      case 'custom': {
        const refIsPartner1 = row.customReferencePartner === 'partner1';
        let refShare: number;
        let otherShare: number;
        if (row.customSplitUnit === 'fixed') {
          const fixed = parseFloat(row.customFixedAmount) || 0;
          refShare = fixed;
          otherShare = Math.max(0, total - fixed);
        } else {
          const pct = parseFloat(row.customPartner1Percentage) || 0;
          refShare = (total * pct) / 100;
          otherShare = (total * (100 - pct)) / 100;
        }
        return refIsPartner1
          ? { partner1: refShare, partner2: otherShare }
          : { partner1: otherShare, partner2: refShare };
      }
      default:
        return null;
    }
  };

  // Obtenir les classes de couleur selon le type, la validation et si c'est avant la date de début
  const getRowColorClasses = (operationType: 'credit' | 'debit', isValidated: boolean, operationDate: string, isInternalTransfer?: boolean) => {
    const isBefore = isBeforeStartDate(operationDate, (selectedAccount?.startDate ?? settings?.jointAccountStartDate) ?? null);

    if (isBefore) {
      return 'bg-gray-100 border-l-4 border-gray-400 opacity-60';
    }

    // Transferts internes en bleu
    if (isInternalTransfer) {
      return isValidated
        ? 'bg-blue-100 border-l-4 border-blue-600'
        : 'bg-blue-50 border-l-4 border-blue-300';
    }

    if (operationType === 'credit') {
      return isValidated
        ? 'bg-green-100 border-l-4 border-green-600'
        : 'bg-green-50 border-l-4 border-green-300';
    } else {
      return isValidated
        ? 'bg-red-100 border-l-4 border-red-600'
        : 'bg-red-50 border-l-4 border-red-300';
    }
  };

  // Obtenir le tooltip pour la barre colorée gauche (remplace la légende)
  const getRowTooltip = (operationType: 'credit' | 'debit', isValidated: boolean, operationDate: string, isInternalTransfer?: boolean): string => {
    const isBefore = isBeforeStartDate(operationDate, (selectedAccount?.startDate ?? settings?.jointAccountStartDate) ?? null);
    if (isBefore) return t('tooltips.beforeStartDate');
    if (isInternalTransfer) return isValidated ? t('tooltips.internalTransferValidated') : t('tooltips.internalTransferPending');
    if (operationType === 'credit') return isValidated ? t('tooltips.creditValidated') : t('tooltips.creditPending');
    return isValidated ? t('tooltips.debitValidated') : t('tooltips.debitPending');
  };

  // Une opération existante peut changer de mois, sauf une série (récurrence,
  // échéancier) ou un transfert interne : les autres occurrences, ou le miroir,
  // resteraient dans le mois d'origine.
  const getMonthLock = (row: OperationRow): 'recurring' | 'transfer' | null => {
    if (row.isNew || !row.id) return null;
    // État enregistré ; à défaut (ligne créée depuis le chargement), la ligne elle-même
    const saved = originalRowsRef.current.get(row.id) ?? row;
    if (saved.isRecurring || saved.recurrenceConfig) return 'recurring';
    if (row.isInternalTransfer) return 'transfer';
    return null;
  };

  // Bornes du champ Date d'une ligne. Une date d'un autre mois est soumise à
  // confirmation (requestMonthChange) plutôt qu'appliquée directement.
  const getDateInputProps = (row: OperationRow) => {
    const lock = getMonthLock(row);
    const accountStartDate = (selectedAccount?.startDate ?? settings?.jointAccountStartDate)?.split('T')[0] ?? null;
    const { minDate, maxDate, minReason } = getOperationDateBounds({
      month: currentMonth,
      year: currentYear,
      lockedToMonth: lock !== null,
      accountStartDate,
      historyStartDate: getHistoryStartDate(planInfo?.limits.historyMonths),
    });
    const lockError = t(lock === 'transfer' ? 'dateErrors.transferLockedToMonth' : 'dateErrors.recurringLockedToMonth');

    return {
      minDate,
      maxDate,
      minDateError: minReason === 'accountStart' && accountStartDate
        ? t('dateErrors.beforeAccountStart', { date: formatShortDate(accountStartDate) })
        : minReason === 'history' ? t('dateErrors.beforeHistoryLimit') : lockError,
      maxDateError: lockError,
      confirmOutside: lock ? undefined : {
        ...getMonthRange(currentMonth, currentYear),
        onRequest: (date: string) => requestMonthChange(row, date),
      },
    };
  };

  const requestMonthChange = (row: OperationRow, date: string) => {
    setMonthChange({
      tempId: row._tempId,
      date,
      isNew: !!row.isNew,
      returnFocus: document.activeElement as HTMLElement | null,
    });
  };

  // Appliquer la date confirmée. Une ligne existante a quitté le mode édition
  // quand le dialogue a pris le focus : on l'enregistre ici.
  const confirmMonthChange = () => {
    if (!monthChange) return;
    const index = rows.findIndex(r => r._tempId === monthChange.tempId);
    if (index === -1) return;
    updateRow(index, 'operationDate', monthChange.date);
    if (!rows[index].isNew) triggerSave();
  };

  const closeMonthChange = () => {
    const returnFocus = monthChange?.returnFocus;
    setMonthChange(null);
    // Rendre le focus au champ Date s'il est encore affiché (ligne en création)
    setTimeout(() => { if (returnFocus?.isConnected) returnFocus.focus(); }, 0);
  };

  // Carte « Prévisionnel » (interactive : sélecteur de jour + impacts budget/enveloppe).
  // Extraite pour être réutilisée à l'identique par le résumé legacy ET la branche Méridien.
  const forecastCard = (
    <div className={`card stat-card stat-card--hero ${forecastBalanceWithBudget() >= 0 ? 'stat-card--forecast' : 'stat-card--forecast-neg'}`}>
      <div className="flex items-center gap-3">
        <TrendingUp className="w-8 h-8 opacity-80" />
        <div className="flex-1">
          <p className="text-sm opacity-80 flex items-center gap-1">{t('summary.forecastBalance')} <HelpTooltip helpKey="forecastBalance" /></p>
          <p className="text-2xl font-bold">{formatCurrency(forecastBalanceWithBudget())}</p>

          {/* Indicateur charges prévisionnelles */}
          {/* Indicateur budget prévisionnel */}
          {hasBudgetCharges && (
            <div className="mt-2 pt-2 border-t border-white/30">
              <div className="flex items-center gap-1 text-xs opacity-80 mb-1">
                <AlertTriangle className="w-3 h-3" />
                <span>{t('summary.budgetForecastIncluded')}</span>
              </div>
              <div className="text-xs opacity-70 space-y-0.5">
                <div className="flex justify-between">
                  <span>{t('summary.balanceWithoutBudget')}</span>
                  <span>{formatCurrency(forecastBalance())}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('summary.remainingBudget')}</span>
                  <span>{formatCurrency(budgetImpact!.totalBudgetImpact)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Indicateur enveloppe (informatif, n'impacte pas le solde) */}
          {hasEnvelopeInfo && envelopeImpact!.envelopes.map(env => (
            <div key={env.envelopeId} className={`mt-2 pt-2 border-t border-white/30`}>
              <div className="text-xs opacity-70 space-y-0.5">
                <div className="flex justify-between">
                  <span>{t('summary.envelopeSavedThisMonth')}</span>
                  <span>{formatCurrency(env.savedThisMonth)} / {formatCurrency(env.recommendedContribution)}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('summary.envelopeSavings')}</span>
                  <span>{formatCurrency(env.cumulativeSavings)} / {formatCurrency(env.targetAmount)}</span>
                </div>
                {env.savingsMode === 'balance' && (
                  <div className="flex justify-between">
                    <span>{t('summary.availableBalance')}</span>
                    <span>{formatCurrency(forecastBalanceWithBudget() - env.cumulativeSavings)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="flex items-center gap-2 mt-2">
            <Calendar className="w-3 h-3 opacity-70" />
            <select
              value={forecastDay}
              onChange={(e) => setForecastDay(parseInt(e.target.value))}
              className="bg-white/20 text-white text-xs rounded px-1 py-0.5 border-0 focus:ring-1 focus:ring-white/50"
            >
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                <option key={day} value={day} className="text-gray-800">
                  {formatDateFr(day, currentMonth, currentYear)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  // Slides du carrousel d'impacts (budgets + enveloppes) — MÊME information que
  // l'ancienne carte prévisionnelle, présentée en carrousel latéral (Méridien).
  const impactSlides: ImpactSlide[] = [];
  if (hasBudgetCharges) {
    impactSlides.push({
      id: 'budget-forecast',
      tone: 'budget',
      title: t('summary.budgetForecastIncluded'),
      rows: [
        { label: t('summary.balanceWithoutBudget'), value: formatCurrency(forecastBalance()) },
        { label: t('summary.remainingBudget'), value: formatCurrency(budgetImpact!.totalBudgetImpact) },
      ],
    });
  }
  if (hasEnvelopeInfo) {
    envelopeImpact!.envelopes.forEach((env) => {
      const rows = [
        { label: t('summary.envelopeSavedThisMonth'), value: `${formatCurrency(env.savedThisMonth)} / ${formatCurrency(env.recommendedContribution)}` },
        { label: t('summary.envelopeSavings'), value: `${formatCurrency(env.cumulativeSavings)} / ${formatCurrency(env.targetAmount)}` },
      ];
      if (env.savingsMode === 'balance') {
        rows.push({ label: t('summary.availableBalance'), value: formatCurrency(forecastBalanceWithBudget() - env.cumulativeSavings) });
      }
      impactSlides.push({ id: `env-${env.envelopeId}`, tone: 'envelope', title: env.envelopeName, rows });
    });
  }

  // Colonne latérale (Méridien) : affichée dès qu'il y a un équilibre à montrer
  // (compte non solo) OU des impacts budgets/enveloppes.
  const showOpsSidebar = isMeridian && (!isJointOnly || impactSlides.length > 0);

  // Prévisionnel (Méridien) : 4ᵉ cellule de la bande de chiffres — grande valeur
  // + sélecteur de jour dessous ; le détail des impacts vit dans le carrousel latéral.
  const forecastFig = (
    <div className="opm-fig opm-fig--fcast">
      <span className="f-lbl">{t('summary.forecastBalance')} <HelpTooltip helpKey="forecastBalance" /></span>
      <span className={`f-val num ${forecastBalanceWithBudget() >= 0 ? '' : 'neg'}`}>{formatCurrency(forecastBalanceWithBudget())}</span>
      <div className="opm-fcast-day">
        <Calendar className="w-4 h-4" aria-hidden />
        <select
          value={forecastDay}
          onChange={(e) => setForecastDay(parseInt(e.target.value))}
          aria-label={t('summary.forecastBalance')}
        >
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => (
            <option key={day} value={day}>{formatDateFr(day, currentMonth, currentYear)}</option>
          ))}
        </select>
      </div>
      {impactSlides.length > 0 && (
        <button
          type="button"
          className="opm-fcast-link"
          onClick={() => {
            setSidebarOpen(true);
            requestAnimationFrame(() =>
              document.getElementById('opm-budgets')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
            );
          }}
        >
          {t('summary.seeBudgets')} →
        </button>
      )}
    </div>
  );

  // Contenu du panneau latéral (équilibre + carrousel budgets), partagé entre la
  // colonne droite (desktop) et le repli au-dessus des opérations (mobile).
  const sidebarContent = showOpsSidebar ? (
    <>
      {!isJointOnly && (
        <EquilibreBar
          partner1Name={partner1Name}
          partner2Name={partner2Name}
          partner1Percentage={incomeProportion.partner1Percentage}
          partner2Percentage={incomeProportion.partner2Percentage}
          partner1Share={(totals.debits * incomeProportion.partner1Percentage) / 100}
          partner2Share={(totals.debits * incomeProportion.partner2Percentage) / 100}
          calculationMode={globalSettings?.calculationMode ?? 'proportional'}
          formatCurrency={formatCurrency}
        />
      )}
      {impactSlides.length > 0 && (
        <div id="opm-budgets">
          <ImpactCarousel title={t('summary.impactsTitle')} slides={impactSlides} />
        </div>
      )}
    </>
  ) : null;

  // Rendu Méridien d'une ligne d'opération (liste <div> grid — mockup validé).
  // Réutilise 100 % des composants de champ et handlers ; la table <table> legacy
  // (Classic/Aurora) n'est pas touchée. Voir §10.9 du cahier design.
  const renderMeridianRow = (row: OperationRow) => {
    const index = rows.findIndex((r) => (r.id && r.id === row.id) || r._tempId === row._tempId);
    const categories = getCategoriesForType(row.operationType);
    const isBefore = !row.isNew && isBeforeStartDate(row.operationDate, (selectedAccount?.startDate ?? settings?.jointAccountStartDate) ?? null);
    const isEditing = row.isNew || editingRowTempId === row._tempId;
    const hasDateError = !!dateErrors[row._tempId];
    const tooltip = !row.isNew ? getRowTooltip(row.operationType, row.isValidated, row.operationDate, row.isInternalTransfer) : '';
    const dataType = row.isInternalTransfer ? 'transfer' : row.operationType;
    const isSelected = !!row.id && selectedRowIds.has(row.id);

    // Cluster de statut — identique en affichage et en édition.
    const statusCluster = (
      <OperationStatusCluster
        isValidated={row.isValidated}
        isRecurring={row.isRecurring}
        includeInCoupleIncome={row.includeInCoupleIncome}
        recurrenceConfig={row.recurrenceConfig}
        operationDate={row.operationDate}
        operationType={row.operationType}
        onToggleValidated={() => handleCheckboxChange(index, 'isValidated', !row.isValidated)}
        onToggleRecurring={() => handleCheckboxChange(index, 'isRecurring', !row.isRecurring)}
        onToggleCoupleIncome={() => handleCheckboxChange(index, 'includeInCoupleIncome', !row.includeInCoupleIncome)}
        onOpenRecurrenceConfig={() => setRecurrenceModalIndex(index)}
        onFocusStop={(e) => e.stopPropagation()}
        showCoupleIncome={isJointOnly}
        disabled={isBefore}
        // La grille Méridien dimensionne la colonne « statut » pour les boutons
        // icône (140px) : les pastilles à texte la feraient déborder.
        compact
      />
    );

    // Boutons d'action (nouvelle ligne dirty / édition d'une ligne existante).
    const actions =
      row.isNew ? (
        <>
          <button type="button" className="opl-btn" onClick={() => handleCancelNewRow(index)}>{t('actions.cancel')}</button>
          <button type="button" className="opl-btn primary" onClick={() => handleValidateNewRow(index)} disabled={!isRowComplete(row)}
            title={isRowComplete(row) ? t('actions.validateAndCreate') : t('actions.fillRequiredFields')}>{t('actions.validate')}</button>
        </>
      ) : !row.isNew && editingRowTempId === row._tempId ? (
        <>
          <button type="button" className="opl-btn" onClick={() => cancelEditMode(row)}>{t('actions.cancel')}</button>
          <button type="button" className="opl-btn primary" onClick={commitEditMode}>{t('actions.validate')}</button>
          <button type="button" className="opl-btn danger" onClick={() => handleDeleteClick(index)} onFocus={(e) => e.stopPropagation()} title={tc('delete')}>
            <Trash2 className="w-4 h-4" />
          </button>
        </>
      ) : null;

    if (isEditing) {
      return (
        <div
          key={row.id || row._tempId}
          className={`opl-edit${row.isNew ? ' animate-slide-down' : ''}`}
          data-scroll-anchor=""
          data-type={dataType}
          onKeyDown={(e) => {
            if (!row.isNew && editingRowTempId === row._tempId) {
              if (e.key === 'Enter') { e.preventDefault(); commitEditMode(); }
              else if (e.key === 'Escape') { e.preventDefault(); cancelEditMode(row); }
            }
            if (row.isNew && !row.isDirty && e.key === 'Escape') { e.preventDefault(); handleCancelNewRow(index); }
          }}
        >
          <div className="opl-edit2">
            <div className="opl-erow opl-erow--main">
              <div className="opl-fld opl-f-date">
                <label>{t('table.date')}</label>
                <DateInput
                  ref={index === 0 && row.isNew ? newRowDateRef : undefined}
                  value={row.operationDate}
                  onChange={(date) => updateRow(index, 'operationDate', date)}
                  onConfirm={row.isNew ? () => handleNewRowFieldConfirm(index) : undefined}
                  onInputStart={row.isNew ? () => markRowDirty(index) : undefined}
                  onValidationError={(error) => setDateErrors((prev) => ({ ...prev, [row._tempId]: error }))}
                  {...getDateInputProps(row)}
                  defaultMonth={currentMonth}
                  defaultYear={currentYear}
                  disabled={isBefore}
                  compact
                />
              </div>
              <div className="opl-fld opl-f-cat">
                <label>{t('table.category')}</label>
                <TreeCategoryAutocomplete
                  categories={categories}
                  categoryId={row.categoryId}
                  subcategoryId={row.subcategoryId}
                  onChange={(catId, subId) => updateRowFields(index, { categoryId: catId, subcategoryId: subId })}
                  onCategoriesChange={(newCats) => {
                    if (row.operationType === 'credit') setIncomeCategories(newCats as Category[]);
                    else setExpenseCategories(newCats as Category[]);
                  }}
                  onConfirm={row.isNew ? () => handleNewRowFieldConfirm(index) : undefined}
                  placeholder={t('placeholders.category')}
                  categoryType={row.operationType === 'credit' ? 'income' : 'expense'}
                  allowCreate={true}
                  forCouple={true}
                  dataTutorial={row.isNew ? 'category-input' : undefined}
                  internalTransferCategoryId={INTERNAL_TRANSFER_CATEGORY_ID}
                  internalTransferSubcategories={sharedBankAccounts
                    .filter((acc) => acc.id !== selectedAccountId)
                    .map((acc) => ({
                      id: acc.id,
                      name: row.operationType === 'debit' ? t('internalTransfer.toAccount', { name: acc.name }) : t('internalTransfer.fromAccount', { name: acc.name }),
                      icon: '↔️',
                      isDefault: false,
                    }))}
                  disabledSubcategoryIds={row.operationType === 'debit' ? envelopeDisabledMap : undefined}
                />
                {row.operationType === 'debit' &&
                  (row.subcategoryId === ENVELOPE_SUBCATEGORY_ID
                    ? filteredEnvelopes.filter((e) => e.status === 'active').length > 0 && (
                        <select value={row.envelopeId || ''} onChange={(e) => updateRow(index, 'envelopeId', e.target.value)}
                          className="mt-1 w-full px-2 py-1 text-xs border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500">
                          <option value="">{t('envelope.noEnvelope')}</option>
                          {filteredEnvelopes.filter((e) => e.status === 'active').map((env) => (<option key={env.id} value={env.id}>{env.name}</option>))}
                        </select>
                      )
                    : filteredEnvelopes.filter((e) => e.status === 'spending').length > 0 && (
                        <select value={row.envelopeId || ''} onChange={(e) => updateRow(index, 'envelopeId', e.target.value)}
                          className="mt-1 w-full px-2 py-1 text-xs border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-violet-500 focus:border-violet-500">
                          <option value="">{t('envelope.noEnvelope')}</option>
                          {filteredEnvelopes.filter((e) => e.status === 'spending').map((env) => (<option key={env.id} value={env.id}>{env.name}</option>))}
                        </select>
                      ))}
                <input type="text" className="input py-1.5 text-sm w-full mt-1" value={row.customLabel}
                  onChange={(e) => updateRow(index, 'customLabel', e.target.value)} placeholder={t('placeholders.detail')} />
              </div>
            </div>
            <div className="opl-right">
              <div className="opl-fld">
                <label>{t('table.amount')}</label>
                <AmountInput
                  value={row.amount}
                  onChange={(val) => updateRow(index, 'amount', val)}
                  onConfirm={row.isNew ? () => handleNewRowFieldConfirm(index) : undefined}
                  className={row.operationType === 'credit' ? '!text-green-600' : '!text-red-600'}
                  budgetHint={row.operationType === 'debit' && row.categoryId ? (
                    <BudgetHint type="shared" categoryId={row.categoryId} subcategoryId={row.subcategoryId || null} bankAccountId={selectedAccountId}
                      month={currentMonth} year={currentYear} operationDate={row.operationDate || undefined} formatCurrency={formatCurrency}
                      currentAmount={parseFloat(row.amount) || 0} excludeOperationId={row.id} />
                  ) : undefined}
                />
                {/* Ligne d'ajout : le type vient du bouton crédit|débit, pas du segmenté */}
                {!row.isNew && (
                  <div className="opl-seg" role="radiogroup" aria-label={t('table.type')}>
                    <button type="button" className={row.operationType === 'credit' ? 'on-credit' : ''} aria-pressed={row.operationType === 'credit'}
                      disabled={isBefore} onClick={() => updateRow(index, 'operationType', 'credit')}>+ {tc('operationType.credit')}</button>
                    <button type="button" className={row.operationType === 'debit' ? 'on-debit' : ''} aria-pressed={row.operationType === 'debit'}
                      disabled={isBefore} onClick={() => updateRow(index, 'operationType', 'debit')}>− {tc('operationType.debit')}</button>
                  </div>
                )}
              </div>
              {actions && <div className="opl-act">{actions}</div>}
            </div>
            <div className="opl-erow opl-erow--extra">
              <div className="opl-fld opl-f-split">
                <label>{t('form.splitType')}</label>
                {row.operationType === 'debit' ? (
                  <>
                    <div className="flex items-center gap-1">
                      <select className="input py-1.5 text-sm w-full" value={row.splitType} onChange={(e) => updateRow(index, 'splitType', e.target.value)}>
                        <option value="proportional">{isCompact ? t('splitTypes.proportionalShort') : t('splitTypes.proportional')}</option>
                        <option value="custom">{t('splitTypes.custom')}</option>
                        <option value="equal">{t('splitTypes.equal')}</option>
                        <option value="partner1_only">{partner1Name}</option>
                        <option value="partner2_only">{partner2Name}</option>
                      </select>
                      {row.splitType === 'custom' && (
                        <button type="button" onClick={() => setCustomSplitModalIndex(index)} onFocus={(e) => e.stopPropagation()}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors" title={t('splitTypes.configureSplit')}>
                          <Settings2 className="w-3.5 h-3.5 text-gray-500 hover:text-primary-600" />
                        </button>
                      )}
                    </div>
                    {(() => {
                      const preview = getSplitPreview(row);
                      if (!preview) return null;
                      return (
                        <div className="flex flex-col leading-tight text-xs text-gray-500 mt-1">
                          <span className="truncate">{partner1Name} : {formatCurrency(preview.partner1)}</span>
                          <span className="truncate">{partner2Name} : {formatCurrency(preview.partner2)}</span>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <select className="input py-1.5 text-sm w-full" value={row.paidBy || ''} onChange={(e) => updateRow(index, 'paidBy', e.target.value)}>
                    <option value="">{t('splitTypes.external')}</option>
                    <option value="partner1">{partner1Name}</option>
                    <option value="partner2">{partner2Name}</option>
                  </select>
                )}
              </div>
              <div className="opl-fld opl-f-status">
                <label>{t('table.status')}</label>
                {statusCluster}
              </div>
            </div>
          </div>
          {hasDateError && <div className="opl-err">{dateErrors[row._tempId]}</div>}
        </div>
      );
    }

    // --- Affichage ---
    const cat = categories.find((c) => c.id === row.categoryId);
    const displayDate = row.operationDate ? (() => { const p = row.operationDate.split('-'); return `${p[2]}/${p[1]}`; })() : '—';
    const amtClass = row.isInternalTransfer ? 'tr' : row.operationType === 'credit' ? 'pos' : 'neg';
    const splitText = row.operationType === 'debit'
      ? (row.splitType === 'proportional' ? (isCompact ? t('splitTypes.proportionalShort') : t('splitTypes.proportional'))
        : row.splitType === 'equal' ? t('splitTypes.equal')
        : row.splitType === 'custom' ? t('splitTypes.custom')
        : row.splitType === 'partner1_only' ? partner1Name
        : row.splitType === 'partner2_only' ? partner2Name : '—')
      : (row.paidBy === 'partner1' ? partner1Name : row.paidBy === 'partner2' ? partner2Name : t('splitTypes.external'));
    return (
      <div
        key={row.id || row._tempId}
        className={`opl-row${isSelected ? ' sel' : ''}`}
        data-type={dataType}
        data-before={isBefore ? '1' : undefined}
        data-tutorial={row.isNew ? 'add-operation' : undefined}
        title={tooltip}
        onClick={() => {
          if (longPressTriggeredRef.current) { longPressTriggeredRef.current = false; return; }
          if (selectedRowIds.size > 0 && row.id) { toggleRowSelection(row.id); return; }
          handleRowFocus(row);
        }}
        onPointerDown={() => {
          if (!row.id) return;
          longPressTriggeredRef.current = false;
          longPressTimerRef.current = setTimeout(() => { longPressTriggeredRef.current = true; toggleRowSelection(row.id!); }, 500);
        }}
        onPointerUp={() => { if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; } }}
        onPointerLeave={() => { if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; } }}
      >
        <div className="opl-tile">
          {row.isInternalTransfer ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M7 10h11l-3-3M17 14H6l3 3" strokeLinecap="round" strokeLinejoin="round" /></svg>
          ) : cat?.icon ? (
            <DynamicIcon name={cat.icon} className="w-[18px] h-[18px]" />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><circle cx="12" cy="12" r="8" /><path d="M12 8v4l2.5 2.5" strokeLinecap="round" /></svg>
          )}
        </div>
        <div className="opl-desc">
          <div className="opl-name">{getCategoryName(row.categoryId, row.operationType) || '—'}</div>
          {row.subcategoryId ? (
            <div className="opl-sub">{getSubcategoryName(row.categoryId, row.subcategoryId, row.operationType)}{row.customLabel ? ` · ${row.customLabel}` : ''}</div>
          ) : row.customLabel ? (
            <div className="opl-sub">{row.customLabel}</div>
          ) : null}
        </div>
        <div><span className="opl-chip">{splitText}</span></div>
        <div className="opl-date num">{displayDate}</div>
        <div className={`opl-amt num ${amtClass}`}>{row.operationType === 'credit' ? '+' : '−'}{formatCurrency(parseFloat(row.amount) || 0)}</div>
        <div className="opl-status" onClick={(e) => e.stopPropagation()}>{statusCluster}</div>
        <button type="button" className="opl-chev" onClick={(e) => { e.stopPropagation(); handleRowFocus(row); }} title={t('table.edit', { defaultValue: 'Modifier' })}>
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-[1400px] mx-auto">
      {/* Toast de sauvegarde en position fixe */}
      <SaveToast status={saveStatus} />

      {/* Header */}
      <div data-tutorial="compte-commun-title" className={isMeridian ? 'opm-head' : undefined}>
        <div>
          <h1 className={isMeridian ? undefined : 'text-2xl font-bold text-gray-900'}>{t('shared.title')}</h1>
          <p className={isMeridian ? undefined : 'text-gray-600'}>{t('shared.subtitle')}</p>
        </div>
      </div>

      {/* Navigation mois */}
      <div className="flex items-center justify-center gap-2 md:gap-4 bg-white rounded-lg p-2 md:p-3 shadow-sm border">
        <button
          onClick={goToPreviousMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <PeriodPicker
          mode="monthly"
          selectedMonth={currentMonth}
          selectedYear={currentYear}
          onSelect={(month, year) => { setCurrentMonth(month); setCurrentYear(year); }}
        />
        <button
          onClick={goToNextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <button
          onClick={fetchData}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors ml-2"
          title={t('actions.refresh')}
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Overlay de limite d'historique */}
      {isPeriodRestricted ? (
        <HistoryLimitOverlay historyMonths={planInfo?.limits.historyMonths ?? 6} />
      ) : (<>

      {/* Résumé */}
      {isMeridian ? (
        <div
          className="opm-figrow"
          style={{ '--opm-cols': isCurrentMonth ? 4 : 3 } as React.CSSProperties}
        >
          <div className="opm-fig">
            <span className="f-lbl">{t('summary.credits')}</span>
            <span className="f-val pos num">{formatCurrency(totals.credits)}</span>
          </div>
          <div className="opm-fig">
            <span className="f-lbl">{t('summary.debits')}</span>
            <span className="f-val neg num">{formatCurrency(totals.debits)}</span>
          </div>
          {isCurrentMonth && (
            <div className="opm-fig">
              <span className="f-lbl">{t('summary.todayBalance')}</span>
              <span className={`f-val num ${todayBalance() >= 0 ? '' : 'neg'}`}>{formatCurrency(todayBalance())}</span>
              <span className="f-sub">{getTodayFormatted()}</span>
            </div>
          )}
          {forecastFig}
        </div>
      ) : (
        <div className={`grid grid-cols-1 gap-4 ${isCurrentMonth ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
          <div className="card stat-card stat-card--credit">
            <div className="flex items-center gap-3">
              <Plus className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-sm opacity-80">{t('summary.credits')}</p>
                <p className="stat-card__value text-2xl font-bold">{formatCurrency(totals.credits)}</p>
              </div>
            </div>
          </div>
          <div className="card stat-card stat-card--debit">
            <div className="flex items-center gap-3">
              <Minus className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-sm opacity-80">{t('summary.debits')}</p>
                <p className="stat-card__value text-2xl font-bold">{formatCurrency(totals.debits)}</p>
              </div>
            </div>
          </div>
          {isCurrentMonth && (
            <div className={`card stat-card ${todayBalance() >= 0 ? 'stat-card--balance' : 'stat-card--balance-neg'}`}>
              <div className="flex items-center gap-3">
                <Wallet className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">{t('summary.todayBalance')}</p>
                  <p className="stat-card__value text-2xl font-bold">{formatCurrency(todayBalance())}</p>
                  <p className="text-xs opacity-70">{getTodayFormatted()}</p>
                </div>
              </div>
            </div>
          )}
          {forecastCard}
        </div>
      )}

      {/* Zone médiane : opérations (gauche) + latéral équilibre/budgets (droite) en Méridien */}
      <div className={showOpsSidebar && !isMobile ? 'opm-mid' : undefined}>
      <div className={isMeridian ? 'opm-mid-main' : undefined}>
      {/* Mobile : équilibre & budgets repliés AU-DESSUS des opérations (fermé par défaut) */}
      {isMeridian && isMobile && sidebarContent && (
        <div className="opm-side-collapse">
          <button
            type="button"
            className="opm-side-toggle"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-expanded={sidebarOpen}
          >
            <span>{t('summary.sidebarToggle')}</span>
            <ChevronDown className={`opm-side-chev${sidebarOpen ? ' open' : ''}`} />
          </button>
          {sidebarOpen && <div className="opm-side opm-side--mobile">{sidebarContent}</div>}
        </div>
      )}

      {/* Tableau des opérations */}
      <div className="card">
        <div className="flex items-center justify-between gap-2 mb-4" data-tutorial="operations-header">
          <h2 className="text-lg font-semibold text-gray-900 min-w-0 truncate">{t('shared.operationsTitle')}</h2>

          <div className="flex items-center gap-2">
            {/* Bouton d'ajout d'opération (crédit | débit) */}
            <AddOperationSplitButton
              activeType={isAddingNew ? newRowType : null}
              onSelect={handleAddOperation}
              className="flex-shrink-0"
              dataTutorial="add-operation"
            />
          </div>
        </div>

        {/* Barre d'actions en masse (desktop) : sur sa propre ligne plutôt
            que dans l'en-tête — la série de boutons débordait du cadre de la
            carte dès que la colonne des opérations se rétrécissait. */}
        {selectedRowIds.size > 0 && !isMobile && (
          <div className="flex items-center justify-between gap-2 flex-wrap bg-primary-50 border border-primary-200 rounded-lg px-3 py-2 mb-3 animate-fade-in">
            <span className="text-sm text-primary-700 font-medium">
              {selectedRowIds.size > 1 ? t('actions.selected_plural', { count: selectedRowIds.size }) : t('actions.selected', { count: selectedRowIds.size })}
            </span>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <button
                onClick={handleBulkToggleValidated}
                className={`flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors ${
                  allSelectedValidated
                    ? 'text-orange-700 hover:bg-orange-100'
                    : 'text-green-700 hover:bg-green-100'
                }`}
                title={allSelectedValidated ? t('actions.markNotValidated') : t('actions.markValidated')}
              >
                <CheckCircle2 className="w-4 h-4" />
                <span className="hidden sm:inline">{allSelectedValidated ? t('actions.invalidate') : t('actions.validate')}</span>
              </button>
              <button
                onClick={handleBulkToggleRecurring}
                className={`flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors ${
                  allSelectedRecurring
                    ? 'text-orange-700 hover:bg-orange-100'
                    : 'text-blue-700 hover:bg-blue-100'
                }`}
                title={allSelectedRecurring ? t('actions.disableRecurrence') : t('actions.enableRecurrence')}
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">{allSelectedRecurring ? t('actions.nonRecurring') : t('actions.recurring')}</span>
              </button>
              <button
                onClick={handleBulkDeleteClick}
                className="flex items-center gap-1 px-2 py-1 text-sm text-red-700 hover:bg-red-100 rounded transition-colors"
                title={t('actions.deleteSelection')}
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">{t('delete', { ns: 'common' })}</span>
              </button>
              <button
                onClick={() => setDuplicateModalOpen(true)}
                className="flex items-center gap-1 px-2 py-1 text-sm text-primary-700 hover:bg-primary-100 rounded transition-colors"
                title={t('actions.duplicate')}
              >
                <Copy className="w-4 h-4" />
                <span className="hidden sm:inline">{t('actions.duplicate')}</span>
              </button>
              <button
                onClick={() => !hasInternalTransfer && setTransferModalOpen(true)}
                className={`flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors ${
                  hasInternalTransfer
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-purple-700 hover:bg-purple-100'
                }`}
                disabled={hasInternalTransfer}
                title={hasInternalTransfer ? t('actions.internalTransferNoTransfer') : t('actions.transferToAccount')}
              >
                <ArrowRightLeft className="w-4 h-4" />
                <span className="hidden sm:inline">{t('actions.transfer')}</span>
              </button>
              <div className="h-4 w-px bg-primary-300" />
              <button
                onClick={clearSelection}
                className="p-1 text-gray-500 hover:bg-gray-100 rounded transition-colors"
                title={t('actions.cancelSelection')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        {/* Barre des brouillons en attente (duplications multiples) */}
        {draftCount >= 2 && (
          <div className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 animate-fade-in">
            <span className="text-sm text-amber-800 font-medium">
              {t(draftCount > 1 ? 'duplicate.pendingDrafts_plural' : 'duplicate.pendingDrafts', { count: draftCount })}
              <span className="hidden sm:inline text-amber-600 font-normal"> — {t('duplicate.pendingHint')}</span>
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleDiscardAllDrafts}
                className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">{t('actions.discardAllDrafts')}</span>
              </button>
              <button
                onClick={handleSaveAllDrafts}
                className="flex items-center gap-1 px-3 py-1 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>{t('actions.saveAllDrafts')}</span>
              </button>
            </div>
          </div>
        )}
        {/* --- VUE LISTE MÉRIDIEN (desktop) --- */}
        {!isMobile && isMeridian && (
        <div className="opl-list-wrap">
          <div className="opl-head">
            <span>
              <button
                type="button"
                className="opl-selall"
                onClick={toggleSelectAll}
                disabled={selectableRows.length === 0}
                title={allSelected ? t('actions.deselectAll') : t('actions.selectAll')}
              >
                {allSelected ? <CheckSquare className="w-4 h-4" /> : someSelected ? <MinusSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              </button>
            </span>
            <span>{t('table.category')}</span>
            <span>{t('form.splitType')}</span>
            <span>{t('table.date')}</span>
            <span className="r">{t('table.amount')}</span>
            <span>{t('table.status')}</span>
            <span></span>
          </div>
          {sortedRows.map(renderMeridianRow)}
        </div>
        )}
        {/* --- VUE TABLEAU (>= 768px) --- */}
        {!isMobile && !isMeridian && (
        <div className="overflow-x-auto">
          <table className="w-full ops-table">
            <thead>
              <tr className="border-b text-left text-sm text-gray-500">
                <th className="pb-3 font-medium w-[36px] xl:w-[40px]">
                  <button
                    onClick={toggleSelectAll}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title={allSelected ? t('actions.deselectAll') : t('actions.selectAll')}
                    disabled={selectableRows.length === 0}
                  >
                    {allSelected ? (
                      <CheckSquare className="w-4 h-4 text-primary-600" />
                    ) : someSelected ? (
                      <MinusSquare className="w-4 h-4 text-primary-400" />
                    ) : (
                      <Square className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </th>
                <SortableHeader
                  label={t('table.date')}
                  sortKey="operationDate"
                  currentSortKey={sortKey}
                  currentSortDirection={sortDirection}
                  onSort={handleSort}
                  className="w-[76px] xl:w-[136px]"
                  suffix={
                    <ColumnFilterDropdown
                      active={isDateFilterActive(filters)}
                      label={t('filters.filterByDate')}
                      onReset={() => setFilters(f => ({ ...f, date: EMPTY_FILTERS.date }))}
                    >
                      <DateFilterContent
                        value={filters.date}
                        onChange={(date) => setFilters(f => ({ ...f, date }))}
                      />
                    </ColumnFilterDropdown>
                  }
                />
                <SortableHeader
                  label={isCompact ? t('table.description').substring(0, 4) + '.' : t('table.description')}
                  sortKey="category"
                  currentSortKey={sortKey}
                  currentSortDirection={sortDirection}
                  onSort={handleSort}
                  dataTutorial="category-column"
                  suffix={
                    <ColumnFilterDropdown
                      active={isDescriptionFilterActive(filters)}
                      label={t('filters.filterByDescription')}
                      onReset={() => setFilters(f => ({ ...f, description: EMPTY_FILTERS.description }))}
                    >
                      <DescriptionFilterContent
                        categories={filterCategories}
                        value={filters.description}
                        onChange={(description) => setFilters(f => ({ ...f, description }))}
                      />
                    </ColumnFilterDropdown>
                  }
                />
                <SortableHeader
                  label={t('table.amount')}
                  sortKey="amount"
                  currentSortKey={sortKey}
                  currentSortDirection={sortDirection}
                  onSort={handleSort}
                  className="w-[140px] xl:w-[160px]"
                  suffix={
                    <ColumnFilterDropdown
                      active={isAmountFilterActive(filters)}
                      label={t('filters.filterByAmount')}
                      onReset={() => setFilters(f => ({ ...f, amount: EMPTY_FILTERS.amount }))}
                    >
                      <AmountFilterContent
                        value={filters.amount}
                        onChange={(amount) => setFilters(f => ({ ...f, amount }))}
                      />
                    </ColumnFilterDropdown>
                  }
                />
                <SortableHeader
                  label={isCompact ? t('splitTypes.splitLabelShort') : t('splitTypes.splitLabel')}
                  sortKey="splitType"
                  currentSortKey={sortKey}
                  currentSortDirection={sortDirection}
                  onSort={handleSort}
                  className="w-[130px] xl:w-[176px]"
                  dataTutorial="split-type"
                  suffix={<HelpTooltip helpKey="splitModes" />}
                />
                <th className="pb-3 font-medium w-[116px] xl:w-[200px] text-left" title={t('table.status')} data-tutorial="validation-checkbox">
                  <div className="flex items-center gap-1">
                    {t('table.status')}
                    <HelpTooltip helpKey="statusColumn" />
                    <ColumnFilterDropdown
                      active={isStatusFilterActive(filters)}
                      label={t('filters.filterByStatus')}
                      onReset={() => setFilters(f => ({ ...f, status: EMPTY_FILTERS.status }))}
                    >
                      <StatusFilterContent
                        value={filters.status}
                        onChange={(status) => setFilters(f => ({ ...f, status }))}
                        show={{ coupleIncome: isJointOnly, splitTypes: true }}
                        splitTypeLabels={splitTypeLabels}
                      />
                    </ColumnFilterDropdown>
                  </div>
                </th>
                <th className="pb-3 w-[52px] xl:w-[56px]"></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => {
                const index = rows.findIndex(r => (r.id && r.id === row.id) || r._tempId === row._tempId);
                const categories = getCategoriesForType(row.operationType);
                const colorClasses = row.isNew ? '' : getRowColorClasses(row.operationType, row.isValidated, row.operationDate, row.isInternalTransfer);
                const tooltip = !row.isNew ? getRowTooltip(row.operationType, row.isValidated, row.operationDate, row.isInternalTransfer) : '';
                const isBefore = !row.isNew && isBeforeStartDate(row.operationDate, (selectedAccount?.startDate ?? settings?.jointAccountStartDate) ?? null);
                const isEditing = row.isNew || editingRowTempId === row._tempId;
                const hasDateError = !!dateErrors[row._tempId];

                return (
                  <React.Fragment key={row.id || row._tempId}>
                  <tr
                    tabIndex={-1}
                    data-scroll-anchor=""
                    className={`${!hasDateError ? 'border-b border-gray-100' : ''} hover:bg-gray-50/50 ${colorClasses} ${isEditing ? 'row-elevated' : 'cursor-pointer'} ${row.isNew ? 'animate-slide-down' : ''} transition-shadow duration-150 row-border-tooltip`}
                    data-tooltip={tooltip}
                    data-tutorial={row.isNew ? 'add-operation' : undefined}
                    onFocus={() => handleRowFocus(row)}
                    onBlur={(e) => handleRowBlur(row._tempId, e as React.FocusEvent<HTMLElement>)}
                    onKeyDown={(e) => {
                      if (!row.isNew && editingRowTempId === row._tempId) {
                        if (e.key === 'Enter') { e.preventDefault(); commitEditMode(); }
                        else if (e.key === 'Escape') { e.preventDefault(); cancelEditMode(row); }
                      }
                      if (row.isNew && !row.isDirty && e.key === 'Escape') {
                        e.preventDefault(); handleCancelNewRow(index);
                      }
                    }}
                  >
                    {/* Checkbox */}
                    <td className="py-2 pr-2">
                      {row.id && !row.isNew ? (
                        <button
                          onClick={() => toggleRowSelection(row.id!)}
                          onFocus={(e) => e.stopPropagation()}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          {selectedRowIds.has(row.id) ? (
                            <CheckSquare className="w-4 h-4 text-primary-600" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      ) : (
                        <div className="w-6 h-6" />
                      )}
                    </td>
                    {/* Date */}
                    <td className="py-2 pr-2 overflow-hidden">
                      {isEditing ? (
                        <>
                          {isBefore && !isCompact && (
                            <span className="inline-flex items-center mr-1" title={t('tooltips.beforeStartDateNotCounted')}>
                              <AlertTriangle className="w-4 h-4 text-gray-500" />
                            </span>
                          )}
                          <DateInput
                            ref={index === 0 && row.isNew ? newRowDateRef : undefined}
                            value={row.operationDate}
                            onChange={(date) => updateRow(index, 'operationDate', date)}
                            onConfirm={row.isNew ? () => handleNewRowFieldConfirm(index) : undefined}
                            onInputStart={row.isNew ? () => markRowDirty(index) : undefined}
                            onValidationError={(error) => setDateErrors(prev => ({ ...prev, [row._tempId]: error }))}
                            {...getDateInputProps(row)}
                            defaultMonth={currentMonth}
                            defaultYear={currentYear}
                            disabled={isBefore}
                            compact={isCompact}
                          />
                        </>
                      ) : (
                        <span
                          className="text-sm text-gray-700 cursor-pointer"
                          onClick={() => handleRowFocus(row)}
                        >
                          {row.operationDate ? (() => {
                            const [y, m, d] = row.operationDate.split('-');
                            return isCompact ? `${d}/${m}` : `${d}/${m}/${y}`;
                          })() : '—'}
                        </span>
                      )}
                    </td>
                    {/* Description (Catégorie + Sous-catégorie + Détail fusionnés) */}
                    <td className="py-2 pr-2 overflow-hidden" data-tutorial={row.isNew ? 'add-row-category' : undefined}>
                      {isEditing ? (
                        <>
                          <TreeCategoryAutocomplete
                            categories={categories}
                            categoryId={row.categoryId}
                            subcategoryId={row.subcategoryId}
                            onChange={(catId, subId) => {
                              updateRowFields(index, { categoryId: catId, subcategoryId: subId });
                            }}
                            onCategoriesChange={(newCats) => {
                              if (row.operationType === 'credit') {
                                setIncomeCategories(newCats as Category[]);
                              } else {
                                setExpenseCategories(newCats as Category[]);
                              }
                            }}
                            onConfirm={row.isNew ? () => handleNewRowFieldConfirm(index) : undefined}
                            placeholder={t('placeholders.category')}
                            categoryType={row.operationType === 'credit' ? 'income' : 'expense'}
                            allowCreate={true}
                            forCouple={true}
                            dataTutorial={row.isNew ? 'category-input' : undefined}
                            internalTransferCategoryId={INTERNAL_TRANSFER_CATEGORY_ID}
                            internalTransferSubcategories={
                              sharedBankAccounts
                                .filter(acc => acc.id !== selectedAccountId)
                                .map(acc => ({
                                  id: acc.id,
                                  name: row.operationType === 'debit' ? t('internalTransfer.toAccount', { name: acc.name }) : t('internalTransfer.fromAccount', { name: acc.name }),
                                  icon: '↔️',
                                  isDefault: false,
                                }))
                            }
                            disabledSubcategoryIds={row.operationType === 'debit' ? envelopeDisabledMap : undefined}
                          />
                          {/* Sélecteur d'enveloppe conditionnel */}
                          {row.operationType === 'debit' && (
                            row.subcategoryId === ENVELOPE_SUBCATEGORY_ID
                              ? filteredEnvelopes.filter(e => e.status === 'active').length > 0 && (
                                <select
                                  value={row.envelopeId || ''}
                                  onChange={(e) => updateRow(index, 'envelopeId', e.target.value)}
                                  className="mt-1 w-full px-2 py-1 text-xs border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                                >
                                  <option value="">{t('envelope.noEnvelope')}</option>
                                  {filteredEnvelopes.filter(e => e.status === 'active').map(env => (
                                    <option key={env.id} value={env.id}>{env.name}</option>
                                  ))}
                                </select>
                              )
                              : filteredEnvelopes.filter(e => e.status === 'spending').length > 0 && (
                                <select
                                  value={row.envelopeId || ''}
                                  onChange={(e) => updateRow(index, 'envelopeId', e.target.value)}
                                  className="mt-1 w-full px-2 py-1 text-xs border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
                                >
                                  <option value="">{t('envelope.noEnvelope')}</option>
                                  {filteredEnvelopes.filter(e => e.status === 'spending').map(env => (
                                    <option key={env.id} value={env.id}>{env.name}</option>
                                  ))}
                                </select>
                              )
                          )}
                          <div className="mt-1">
                            <input
                              type="text"
                              className="input py-1.5 text-sm w-full"
                              value={row.customLabel}
                              onChange={(e) => updateRow(index, 'customLabel', e.target.value)}
                              placeholder={t('placeholders.detail')}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="cursor-pointer" onClick={() => handleRowFocus(row)}>
                          <span className="text-sm font-semibold text-gray-900 truncate block">
                            {getCategoryName(row.categoryId, row.operationType) || '—'}
                          </span>
                          {row.subcategoryId && (
                            <span className="text-xs text-gray-500 truncate block">
                              {getSubcategoryName(row.categoryId, row.subcategoryId, row.operationType)}
                            </span>
                          )}
                          {row.customLabel && (
                            <span className="text-xs italic text-gray-400 truncate block">
                              {row.customLabel}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Montant */}
                    <td className="py-2 pr-2 overflow-visible">
                      {isEditing ? (
                        <div className="flex items-stretch">
                          {/* Ligne d'ajout : le type vient du bouton crédit|débit, pas du toggle */}
                          {!row.isNew && (
                            <VerticalTypeToggle
                              value={row.operationType}
                              onChange={(val) => updateRow(index, 'operationType', val)}
                              disabled={isBefore}
                            />
                          )}
                          <AmountInput
                            value={row.amount}
                            onChange={(val) => updateRow(index, 'amount', val)}
                            onConfirm={row.isNew ? () => handleNewRowFieldConfirm(index) : undefined}
                            className={`flex-1 ${row.isNew ? '' : '!rounded-l-none !border-l-0'} ${row.operationType === 'credit' ? '!text-green-600' : '!text-red-600'}`}
                            budgetHint={
                              row.operationType === 'debit' && row.categoryId ? (
                                <BudgetHint
                                  type="shared"
                                  categoryId={row.categoryId}
                                  subcategoryId={row.subcategoryId || null}
                                  bankAccountId={selectedAccountId}
                                  month={currentMonth}
                                  year={currentYear}
                                  operationDate={row.operationDate || undefined}
                                  formatCurrency={formatCurrency}
                                  currentAmount={parseFloat(row.amount) || 0}
                                  excludeOperationId={row.id}
                                />
                              ) : undefined
                            }
                          />
                        </div>
                      ) : (
                        <span
                          className={`ops-amount text-sm font-bold cursor-pointer ${row.operationType === 'credit' ? 'text-green-600' : 'text-red-600'}`}
                          onClick={() => handleRowFocus(row)}
                        >
                          {row.operationType === 'credit' ? '+' : '−'}{formatCurrency(parseFloat(row.amount) || 0)}
                        </span>
                      )}
                    </td>
                    {/* Répartition */}
                    <td className="py-2 pr-2 overflow-hidden">
                      {isEditing ? (
                        row.operationType === 'debit' ? (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1">
                              <select
                                className="input py-1.5 text-sm w-full"
                                value={row.splitType}
                                onChange={(e) => updateRow(index, 'splitType', e.target.value)}
                              >
                                <option value="proportional">{isCompact ? t('splitTypes.proportionalShort') : t('splitTypes.proportional')}</option>
                                <option value="custom">{t('splitTypes.custom')}</option>
                                <option value="equal">{t('splitTypes.equal')}</option>
                                <option value="partner1_only">{partner1Name}</option>
                                <option value="partner2_only">{partner2Name}</option>
                              </select>
                              {row.splitType === 'custom' && (
                                <button
                                  type="button"
                                  onClick={() => setCustomSplitModalIndex(index)}
                                  onFocus={(e) => e.stopPropagation()}
                                  className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                                  title={t('splitTypes.configureSplit')}
                                >
                                  <Settings2 className="w-3.5 h-3.5 text-gray-500 hover:text-primary-600" />
                                </button>
                              )}
                            </div>
                            {(() => {
                              const preview = getSplitPreview(row);
                              if (!preview) return null;
                              return (
                                <div className="flex flex-col leading-tight text-xs text-gray-500 max-w-[140px]">
                                  <span className="truncate" title={`${partner1Name} : ${formatCurrency(preview.partner1)}`}>{partner1Name} : {formatCurrency(preview.partner1)}</span>
                                  <span className="truncate" title={`${partner2Name} : ${formatCurrency(preview.partner2)}`}>{partner2Name} : {formatCurrency(preview.partner2)}</span>
                                </div>
                              );
                            })()}
                          </div>
                        ) : (
                          <select
                            className="input py-1.5 text-sm w-full"
                            value={row.paidBy || ''}
                            onChange={(e) => updateRow(index, 'paidBy', e.target.value)}
                          >
                            <option value="">{t('splitTypes.external')}</option>
                            <option value="partner1">{partner1Name}</option>
                            <option value="partner2">{partner2Name}</option>
                          </select>
                        )
                      ) : (
                        <span
                          className="text-sm text-gray-700 cursor-pointer"
                          onClick={() => handleRowFocus(row)}
                        >
                          {row.operationType === 'debit' ? (
                            row.splitType === 'proportional' ? (isCompact ? t('splitTypes.proportionalShort') : t('splitTypes.proportional')) :
                            row.splitType === 'equal' ? t('splitTypes.equal') :
                            row.splitType === 'custom' ? t('splitTypes.custom') :
                            row.splitType === 'partner1_only' ? partner1Name :
                            row.splitType === 'partner2_only' ? partner2Name : '—'
                          ) : (
                            row.paidBy === 'partner1' ? partner1Name :
                            row.paidBy === 'partner2' ? partner2Name : t('splitTypes.external')
                          )}
                        </span>
                      )}
                    </td>
                    {/* Statut (pills ou icônes selon compact) */}
                    <td className="py-2 text-left">
                      <OperationStatusCluster
                        isValidated={row.isValidated}
                        isRecurring={row.isRecurring}
                        includeInCoupleIncome={row.includeInCoupleIncome}
                        recurrenceConfig={row.recurrenceConfig}
                        operationDate={row.operationDate}
                        operationType={row.operationType}
                        onToggleValidated={() => handleCheckboxChange(index, 'isValidated', !row.isValidated)}
                        onToggleRecurring={() => handleCheckboxChange(index, 'isRecurring', !row.isRecurring)}
                        onToggleCoupleIncome={() => handleCheckboxChange(index, 'includeInCoupleIncome', !row.includeInCoupleIncome)}
                        onOpenRecurrenceConfig={() => setRecurrenceModalIndex(index)}
                        onFocusStop={(e) => e.stopPropagation()}
                        showCoupleIncome={isJointOnly}
                        disabled={isBefore}
                        compact={isCompact}
                      />
                    </td>
                    {/* Actions / Chevron */}
                    <td className="py-2">
                      {row.isNew ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleCancelNewRow(index)}
                            className="p-1 hover:bg-red-50 rounded text-red-500"
                            title={t('actions.cancel')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleValidateNewRow(index)}
                            disabled={!isRowComplete(row)}
                            className={`p-1 rounded ${
                              isRowComplete(row)
                                ? 'hover:bg-green-50 text-green-600'
                                : 'text-gray-300 cursor-not-allowed'
                            }`}
                            title={isRowComplete(row) ? t('actions.validateAndCreate') : t('actions.fillRequiredFields')}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        </div>
                      ) : !row.isNew && editingRowTempId === row._tempId ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => cancelEditMode(row)}
                            className="p-1 hover:bg-red-50 rounded text-red-500"
                            title={t('actions.cancelChanges')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <button
                            onClick={commitEditMode}
                            className="p-1 hover:bg-green-50 rounded text-green-600"
                            title={t('actions.validateChanges')}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(index)}
                            onFocus={(e) => e.stopPropagation()}
                            className="p-1 hover:bg-red-50 rounded text-red-500"
                            title={tc('delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : !row.isNew ? (
                        <ChevronDown
                          className="w-4 h-4 text-gray-400 cursor-pointer"
                          onClick={() => handleRowFocus(row)}
                        />
                      ) : null}
                    </td>
                  </tr>
                  {dateErrors[row._tempId] && (
                    <tr className="bg-amber-50 border-b border-gray-100">
                      <td></td>
                      <td colSpan={6} className="py-1 text-xs text-red-700">
                        {dateErrors[row._tempId]}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        )}

        {/* --- VUE CARTES MOBILE (< 900px) --- */}
        {isMobile && (
        <div>
          {/* Barre de tri mobile */}
          <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
            <span className="font-medium">{t('sort.label')}</span>
            <select
              value={sortKey}
              onChange={(e) => {
                const newKey = e.target.value;
                if (newKey !== sortKey) {
                  setSortKey(newKey);
                  setSortDirection('asc');
                }
              }}
              className="input py-1 px-2 text-sm"
            >
              <option value="operationDate">{t('mobileSort.date')}</option>
              <option value="operationType">{t('mobileSort.type')}</option>
              <option value="category">{t('mobileSort.category')}</option>
              <option value="amount">{t('mobileSort.amount')}</option>
            </select>
            <button
              onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              title={sortDirection === 'asc' ? t('sort.ascending') : t('sort.descending')}
            >
              {sortDirection === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          {/* Liste de cartes */}
          <div className="space-y-2">
            {sortedRows.map((row) => {
              const index = rows.findIndex(r => (r.id && r.id === row.id) || r._tempId === row._tempId);
              const categories = getCategoriesForType(row.operationType);
              const colorClasses = row.isNew ? '' : getRowColorClasses(row.operationType, row.isValidated, row.operationDate, row.isInternalTransfer);
              const isBefore = !row.isNew && isBeforeStartDate(row.operationDate, (selectedAccount?.startDate ?? settings?.jointAccountStartDate) ?? null);
              const hasDateError = !!dateErrors[row._tempId];
              const isExpanded = row.isNew || editingRowTempId === row._tempId || expandedMobileIds.has(row._tempId);

              return (
                <div
                  key={row.id || row._tempId}
                  tabIndex={-1}
                  className={`rounded-lg border p-3 transition-all select-none ${colorClasses || 'border-gray-200 bg-white'} ${row.isNew ? 'animate-slide-down' : ''} ${isExpanded && !row.isNew ? 'row-elevated' : ''}`}
                  data-scroll-anchor=""
                  data-tutorial={row.isNew ? 'add-operation' : undefined}
                  onFocus={() => handleRowFocus(row)}
                  onBlur={(e) => handleRowBlur(row._tempId, e as React.FocusEvent<HTMLElement>)}
                  onKeyDown={(e) => {
                    if (!row.isNew && editingRowTempId === row._tempId) {
                      if (e.key === 'Enter') { e.preventDefault(); commitEditMode(); }
                      else if (e.key === 'Escape') { e.preventDefault(); cancelEditMode(row); }
                    }
                    if (row.isNew && !row.isDirty && e.key === 'Escape') {
                      e.preventDefault(); handleCancelNewRow(index);
                    }
                  }}
                >
                  {/* Ligne résumé (toujours visible) */}
                  <div
                    className={`flex items-center gap-2 ${selectedRowIds.size > 0 && row.id && selectedRowIds.has(row.id) ? 'ring-2 ring-primary-400 rounded-md' : ''}`}
                    onClick={() => {
                      if (longPressTriggeredRef.current) { longPressTriggeredRef.current = false; return; }
                      if (row.isNew || editingRowTempId === row._tempId) return;
                      // En mode sélection, un tap sélectionne/désélectionne
                      if (selectedRowIds.size > 0 && row.id) {
                        toggleRowSelection(row.id);
                        return;
                      }
                      setExpandedMobileIds(prev => {
                        const next = new Set(prev);
                        next.has(row._tempId) ? next.delete(row._tempId) : next.add(row._tempId);
                        return next;
                      });
                    }}
                    onTouchStart={() => {
                      if (row.isNew || !row.id || selectedRowIds.size > 0) return;
                      longPressTriggeredRef.current = false;
                      longPressTimerRef.current = setTimeout(() => {
                        longPressTriggeredRef.current = true;
                        toggleRowSelection(row.id!);
                      }, 500);
                    }}
                    onTouchEnd={() => {
                      if (longPressTimerRef.current) {
                        clearTimeout(longPressTimerRef.current);
                        longPressTimerRef.current = null;
                      }
                    }}
                    onTouchMove={() => {
                      if (longPressTimerRef.current) {
                        clearTimeout(longPressTimerRef.current);
                        longPressTimerRef.current = null;
                      }
                    }}
                  >
                    {/* Indicateur de sélection */}
                    {selectedRowIds.size > 0 && row.id && !row.isNew && (
                      <div className="flex-shrink-0 self-start pt-1">
                        {selectedRowIds.has(row.id) ? (
                          <CheckSquare className="w-4 h-4 text-primary-600" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    )}

                    {/* Contenu principal */}
                    {!row.isNew && (
                      <div className="flex-1 min-w-0">
                        {/* Ligne 1 : Date + Catégorie + Montant */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 flex-shrink-0 w-12">
                            {row.operationDate ? `${row.operationDate.split('-')[2]}/${row.operationDate.split('-')[1]}` : '—'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium truncate block">
                              {getCategoryName(row.categoryId, row.operationType) || '—'}
                            </span>
                            {row.subcategoryId && (
                              <span className="text-xs text-gray-400 truncate block">
                                {getSubcategoryName(row.categoryId, row.subcategoryId, row.operationType)}
                              </span>
                            )}
                            {row.customLabel && (
                              <span className="text-xs italic text-gray-400 truncate block">
                                {row.customLabel}
                              </span>
                            )}
                          </div>
                          <span className={`text-sm font-semibold flex-shrink-0 ${
                            row.operationType === 'credit' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {row.operationType === 'credit' ? '+' : '−'}
                            {formatCurrency(parseFloat(row.amount) || 0)}
                          </span>
                        </div>
                        {/* Ligne 2 : Icônes statut + Chevron */}
                        {!isExpanded && (
                          <div className="flex items-center justify-between mt-1">
                            <div className="flex items-center gap-1">
                              {row.isValidated && <CheckCircle2 className="w-3.5 h-3.5 text-primary-500" />}
                              {row.includeInCoupleIncome && <Users className="w-3.5 h-3.5 text-purple-500" />}
                              {row.isRecurring && <RefreshCw className="w-3.5 h-3.5 text-blue-500" />}
                              {/* Accès direct à la configuration sans avoir à déplier la carte */}
                              {canConfigureRecurrence(row.isRecurring, row.recurrenceConfig) && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setRecurrenceModalIndex(index); }}
                                  onFocus={(e) => e.stopPropagation()}
                                  className="p-1 rounded-full text-gray-400 hover:text-primary-600 hover:bg-gray-100 transition-colors"
                                  title={tc('statusCluster.configureRecurrence')}
                                  aria-label={tc('statusCluster.configureRecurrence')}
                                >
                                  <Settings2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                            <ChevronDown className="w-4 h-4 text-gray-400 transition-transform" />
                          </div>
                        )}
                        {isExpanded && (
                          <div className="flex justify-end mt-1">
                            <ChevronDown className="w-4 h-4 text-gray-400 transition-transform rotate-180" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Contenu déplié */}
                  {isExpanded && (
                    <div className={`${!row.isNew ? 'mt-3 pt-3 border-t border-gray-100' : ''} animate-fade-in space-y-2`}>
                      {/* Date */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <DateInput
                            ref={index === 0 && row.isNew ? newRowDateRef : undefined}
                            value={row.operationDate}
                            onChange={(date) => updateRow(index, 'operationDate', date)}
                            onConfirm={row.isNew ? () => handleNewRowFieldConfirm(index) : undefined}
                            onInputStart={row.isNew ? () => markRowDirty(index) : undefined}
                            onValidationError={(error) => setDateErrors(prev => ({ ...prev, [row._tempId]: error }))}
                            {...getDateInputProps(row)}
                            defaultMonth={currentMonth}
                            defaultYear={currentYear}
                            disabled={isBefore}
                            compact={false}
                          />
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {row.isNew ? (
                            <>
                              <button onClick={() => handleCancelNewRow(index)} className="p-1 hover:bg-red-50 rounded text-red-500" title={t('actions.cancel')}>
                                <X className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleValidateNewRow(index)}
                                disabled={!isRowComplete(row)}
                                className={`p-1 rounded ${isRowComplete(row) ? 'hover:bg-green-50 text-green-600' : 'text-gray-300 cursor-not-allowed'}`}
                                title={isRowComplete(row) ? t('actions.validateAndCreate') : t('actions.fillRequired')}
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            </>
                          ) : !row.isNew && editingRowTempId === row._tempId ? (
                            <>
                              <button onClick={() => handleDeleteClick(index)} onFocus={(e) => e.stopPropagation()} className="p-1 hover:bg-red-50 rounded text-red-500" title={tc('delete')}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                              <button onClick={() => cancelEditMode(row)} className="p-1 hover:bg-red-50 rounded text-red-500" title={t('actions.cancel')}>
                                <X className="w-4 h-4" />
                              </button>
                              <button onClick={commitEditMode} className="p-1 hover:bg-green-50 rounded text-green-600" title={t('actions.validate')}>
                                <Check className="w-4 h-4" />
                              </button>
                            </>
                          ) : !row.isNew ? (
                            <button
                              onClick={() => handleDeleteClick(index)}
                              onFocus={(e) => e.stopPropagation()}
                              className="p-1.5 hover:bg-red-50 rounded text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          ) : null}
                        </div>
                      </div>

                      {/* Catégorie + Sous-catégorie */}
                      <div data-tutorial={row.isNew ? 'add-row-category' : undefined}>
                        <TreeCategoryAutocomplete
                          categories={categories}
                          categoryId={row.categoryId}
                          subcategoryId={row.subcategoryId}
                          onChange={(catId, subId) => {
                            updateRowFields(index, { categoryId: catId, subcategoryId: subId });
                          }}
                          onCategoriesChange={(newCats) => {
                            if (row.operationType === 'credit') {
                              setIncomeCategories(newCats as Category[]);
                            } else {
                              setExpenseCategories(newCats as Category[]);
                            }
                          }}
                          onConfirm={row.isNew ? () => handleNewRowFieldConfirm(index) : undefined}
                          placeholder={t('placeholders.category')}
                          categoryType={row.operationType === 'credit' ? 'income' : 'expense'}
                          allowCreate={true}
                          forCouple={true}
                          dataTutorial={row.isNew ? 'category-input' : undefined}
                          internalTransferCategoryId={INTERNAL_TRANSFER_CATEGORY_ID}
                          internalTransferSubcategories={
                            sharedBankAccounts
                              .filter(acc => acc.id !== selectedAccountId)
                              .map(acc => ({
                                id: acc.id,
                                name: row.operationType === 'debit' ? t('internalTransfer.toAccount', { name: acc.name }) : t('internalTransfer.fromAccount', { name: acc.name }),
                                icon: '↔️',
                                isDefault: false,
                              }))
                          }
                          disabledSubcategoryIds={row.operationType === 'debit' ? envelopeDisabledMap : undefined}
                        />
                      </div>

                      {/* Sélecteur d'enveloppe conditionnel (mobile) */}
                      {row.operationType === 'debit' && (
                        row.subcategoryId === ENVELOPE_SUBCATEGORY_ID
                          ? filteredEnvelopes.filter(e => e.status === 'active').length > 0 && (
                            <select
                              value={row.envelopeId || ''}
                              onChange={(e) => updateRow(index, 'envelopeId', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                            >
                              <option value="">{t('envelope.noEnvelope')}</option>
                              {filteredEnvelopes.filter(e => e.status === 'active').map(env => (
                                <option key={env.id} value={env.id}>{env.name}</option>
                              ))}
                            </select>
                          )
                          : filteredEnvelopes.filter(e => e.status === 'spending').length > 0 && (
                            <select
                              value={row.envelopeId || ''}
                              onChange={(e) => updateRow(index, 'envelopeId', e.target.value)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
                            >
                              <option value="">{t('envelope.noEnvelope')}</option>
                              {filteredEnvelopes.filter(e => e.status === 'spending').map(env => (
                                <option key={env.id} value={env.id}>{env.name}</option>
                              ))}
                            </select>
                          )
                      )}

                      {/* Détail */}
                      <input
                        type="text"
                        className="input py-1.5 text-sm w-full"
                        value={row.customLabel}
                        onChange={(e) => updateRow(index, 'customLabel', e.target.value)}
                        placeholder={t('placeholders.detail')}
                      />

                      {/* Montant (avec toggle type) + Répartition */}
                      <div className="flex items-center gap-2">
                        <div className="w-32 flex-shrink-0 flex items-stretch">
                          {/* Ligne d'ajout : le type vient du bouton crédit|débit, pas du toggle */}
                          {!row.isNew && (
                            <VerticalTypeToggle
                              value={row.operationType}
                              onChange={(val) => updateRow(index, 'operationType', val)}
                              disabled={isBefore}
                            />
                          )}
                          <AmountInput
                            value={row.amount}
                            onChange={(val) => updateRow(index, 'amount', val)}
                            onConfirm={row.isNew ? () => handleNewRowFieldConfirm(index) : undefined}
                            className={`flex-1 ${row.isNew ? '' : '!rounded-l-none !border-l-0'} ${row.operationType === 'credit' ? '!text-green-600' : '!text-red-600'}`}
                            budgetHint={
                              row.operationType === 'debit' && row.categoryId ? (
                                <BudgetHint
                                  type="shared"
                                  categoryId={row.categoryId}
                                  subcategoryId={row.subcategoryId || null}
                                  bankAccountId={selectedAccountId}
                                  month={currentMonth}
                                  year={currentYear}
                                  operationDate={row.operationDate || undefined}
                                  formatCurrency={formatCurrency}
                                  currentAmount={parseFloat(row.amount) || 0}
                                  excludeOperationId={row.id}
                                />
                              ) : undefined
                            }
                          />
                        </div>
                        <div className="flex-1 min-w-0" data-tutorial={row.isNew ? 'split-type' : undefined}>
                          {row.operationType === 'debit' ? (
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1">
                                <select
                                  className="input py-1.5 text-sm w-full"
                                  value={row.splitType}
                                  onChange={(e) => updateRow(index, 'splitType', e.target.value)}
                                >
                                  <option value="proportional">{t('splitTypes.proportionalShort')}</option>
                                  <option value="custom">{t('splitTypes.custom')}</option>
                                  <option value="equal">{t('splitTypes.equal')}</option>
                                  <option value="partner1_only">{partner1Name}</option>
                                  <option value="partner2_only">{partner2Name}</option>
                                </select>
                                {row.splitType === 'custom' && (
                                  <button
                                    type="button"
                                    onClick={() => setCustomSplitModalIndex(index)}
                                    onFocus={(e) => e.stopPropagation()}
                                    className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                                    title={t('splitTypes.configureSplit')}
                                  >
                                    <Settings2 className="w-3.5 h-3.5 text-gray-500" />
                                  </button>
                                )}
                              </div>
                              {(() => {
                                const preview = getSplitPreview(row);
                                if (!preview) return null;
                                return (
                                  <div className="flex flex-col leading-tight text-xs text-gray-500">
                                    <span className="truncate" title={`${partner1Name} : ${formatCurrency(preview.partner1)}`}>{partner1Name} : {formatCurrency(preview.partner1)}</span>
                                    <span className="truncate" title={`${partner2Name} : ${formatCurrency(preview.partner2)}`}>{partner2Name} : {formatCurrency(preview.partner2)}</span>
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <select
                              className="input py-1.5 text-sm w-full"
                              value={row.paidBy || ''}
                              onChange={(e) => updateRow(index, 'paidBy', e.target.value)}
                            >
                              <option value="">{t('splitTypes.external')}</option>
                              <option value="partner1">{partner1Name}</option>
                              <option value="partner2">{partner2Name}</option>
                            </select>
                          )}
                        </div>
                      </div>

                      {/* Statut (pills) */}
                      <div className="border-t border-gray-100 pt-2">
                        <OperationStatusCluster
                          isValidated={row.isValidated}
                          isRecurring={row.isRecurring}
                          includeInCoupleIncome={row.includeInCoupleIncome}
                          recurrenceConfig={row.recurrenceConfig}
                          operationDate={row.operationDate}
                          operationType={row.operationType}
                          onToggleValidated={() => handleCheckboxChange(index, 'isValidated', !row.isValidated)}
                          onToggleRecurring={() => handleCheckboxChange(index, 'isRecurring', !row.isRecurring)}
                          onToggleCoupleIncome={() => handleCheckboxChange(index, 'includeInCoupleIncome', !row.includeInCoupleIncome)}
                          onOpenRecurrenceConfig={() => setRecurrenceModalIndex(index)}
                          onFocusStop={(e) => e.stopPropagation()}
                          showCoupleIncome={isJointOnly}
                          disabled={isBefore}
                          variant="mobile"
                        />
                      </div>

                      {/* Erreur date */}
                      {hasDateError && (
                        <div className="text-xs text-red-700 bg-amber-50 rounded px-2 py-1">
                          {dateErrors[row._tempId]}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}
      </div>
      </div>{/* /opm-mid-main */}
      {showOpsSidebar && !isMobile && (
        <aside className="opm-side">{sidebarContent}</aside>
      )}
      </div>{/* /opm-mid */}

      {/* Barre d'actions en masse (mobile sticky bottom) */}
      {isMobile && selectedRowIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg px-4 py-3 animate-fade-in">
          <div className="flex items-center justify-between gap-2 flex-wrap max-w-[1400px] mx-auto">
            <span className="text-sm text-primary-700 font-medium flex-shrink-0">
              {t(selectedRowIds.size > 1 ? 'actions.selected_plural' : 'actions.selected', { count: selectedRowIds.size })}
            </span>
            <div className="flex items-center gap-1 flex-wrap justify-end">
              <button
                onClick={handleBulkToggleValidated}
                className={`p-2 rounded-lg transition-colors ${
                  allSelectedValidated ? 'text-orange-700 bg-orange-50' : 'text-green-700 bg-green-50'
                }`}
                title={allSelectedValidated ? t('actions.invalidate') : t('actions.validate')}
              >
                <CheckCircle2 className="w-5 h-5" />
              </button>
              <button
                onClick={handleBulkToggleRecurring}
                className={`p-2 rounded-lg transition-colors ${
                  allSelectedRecurring ? 'text-orange-700 bg-orange-50' : 'text-blue-700 bg-blue-50'
                }`}
                title={allSelectedRecurring ? t('actions.nonRecurring') : t('actions.recurring')}
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={handleBulkDeleteClick}
                className="p-2 rounded-lg text-red-700 bg-red-50 transition-colors"
                title={tc('delete')}
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setDuplicateModalOpen(true)}
                className="p-2 rounded-lg text-primary-700 bg-primary-50 transition-colors"
                title={t('actions.duplicate')}
              >
                <Copy className="w-5 h-5" />
              </button>
              <button
                onClick={() => !hasInternalTransfer && setTransferModalOpen(true)}
                className={`p-2 rounded-lg transition-colors ${
                  hasInternalTransfer ? 'text-gray-400 bg-gray-50' : 'text-purple-700 bg-purple-50'
                }`}
                disabled={hasInternalTransfer}
                title={t('actions.transfer')}
              >
                <ArrowRightLeft className="w-5 h-5" />
              </button>
              <div className="w-px h-6 bg-gray-300 mx-1" />
              <button
                onClick={() => { clearSelection(); toggleSelectAll(); }}
                className="p-2 rounded-lg text-primary-700 bg-primary-50 transition-colors"
                title={allSelected ? t('actions.deselectAll') : t('actions.selectAll')}
              >
                {allSelected ? <MinusSquare className="w-5 h-5" /> : <CheckSquare className="w-5 h-5" />}
              </button>
              <button
                onClick={clearSelection}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                title={t('actions.cancelSelection')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
        <strong>{t('info.tip')}</strong> {t('info.tipShared')} <Check className="w-3 h-3 inline mx-1 text-green-600" /> {t('info.tipSharedValidate')} <X className="w-3 h-3 inline mx-1 text-red-500" /> {t('info.tipSharedCancel')}<br />
        {t('info.tipAdd')} <Plus className="w-3 h-3 inline mx-1" /> <strong>{t('info.tipAddBold')}</strong> {t('info.tipAddSuffix')}<br />
        {t('info.tipStatus')} <CheckCircle2 className="w-3 h-3 inline mx-1 text-primary-600" /> {t('info.tipStatusValidated')} <RefreshCw className="w-3 h-3 inline mx-1 text-blue-600" /> {t('info.tipStatusRecurring')}
      </div>

      {/* Dialogue de suppression pour éléments récurrents */}
      {deleteDialog.isRecurring ? (
        <RecurringDeleteDialog
          isOpen={deleteDialog.isOpen}
          onClose={() => setDeleteDialog({ isOpen: false, rowIndex: null, rowId: null, isRecurring: false })}
          onDeleteAllFuture={handleDeleteAllFuture}
          itemType={t('title').toLowerCase()}
        />
      ) : (
        deleteDialog.isOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setDeleteDialog({ isOpen: false, rowIndex: null, rowId: null, isRecurring: false })}
            />
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('deleteDialog.deleteOperation')}</h3>
                <p className="text-gray-600 mb-6">{t('deleteDialog.deleteConfirmSingle')}</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setDeleteDialog({ isOpen: false, rowIndex: null, rowId: null, isRecurring: false })}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {tc('cancel')}
                  </button>
                  <button
                    onClick={handleDeleteSingle}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    {tc('delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* Dialogue de suppression en masse */}
      {bulkDeleteDialog.hasRecurring ? (
        <RecurringDeleteDialog
          isOpen={bulkDeleteDialog.isOpen}
          onClose={() => setBulkDeleteDialog({ isOpen: false, hasRecurring: false })}
          onDeleteAllFuture={handleBulkDeleteAllFuture}
          itemType={t(selectedRowIds.size > 1 ? 'deleteDialog.deleteCount_plural' : 'deleteDialog.deleteCount', { count: selectedRowIds.size })}
        />
      ) : (
        bulkDeleteDialog.isOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setBulkDeleteDialog({ isOpen: false, hasRecurring: false })}
            />
            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t(selectedRowIds.size > 1 ? 'deleteDialog.deleteCount_plural' : 'deleteDialog.deleteCount', { count: selectedRowIds.size })}</h3>
                <p className="text-gray-600 mb-6">{selectedRowIds.size > 1 ? t('deleteDialog.deleteConfirmPlural') : t('deleteDialog.deleteConfirmSingleItem')}</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setBulkDeleteDialog({ isOpen: false, hasRecurring: false })}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {tc('cancel')}
                  </button>
                  <button
                    onClick={handleBulkDeleteAllFuture}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    {tc('delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* Dialogue de sauvegarde pour opérations récurrentes */}
      <RecurringSaveDialog
        isOpen={recurringSaveDialog.isOpen}
        onClose={() => {
          setRecurringSaveDialog({ isOpen: false });
          // Si l'utilisateur a annulé (pas cliqué sur sauvegarder), rétablir la ligne originale
          if (pendingRecurringSaveRowIdRef.current) {
            const rowId = pendingRecurringSaveRowIdRef.current;
            const original = originalRowsRef.current.get(rowId);
            if (original) {
              setRows(prevRows => {
                const newRows = [...prevRows];
                const idx = newRows.findIndex(r => r.id === rowId);
                if (idx !== -1) { newRows[idx] = { ...original, isDirty: false }; }
                rowsRef.current = newRows;
                return newRows;
              });
            }
            pendingRecurringSaveRowIdRef.current = null;
          }
        }}
        onSaveSingle={() => {
          pendingRecurringSaveRowIdRef.current = null;
          propagateToFutureRef.current = false;
          triggerSave();
        }}
        onSaveAllFuture={() => {
          pendingRecurringSaveRowIdRef.current = null;
          propagateToFutureRef.current = true;
          triggerSave();
        }}
      />

      {/* Confirmation : la date saisie place l'opération dans un autre mois */}
      <ConfirmDialog
        isOpen={!!monthChange}
        variant="primary"
        autoFocusConfirm
        title={monthChange ? t(monthChange.isNew ? 'monthChange.createTitle' : 'monthChange.moveTitle', {
          month: formatMonthLabel(monthChange.date, i18n.language),
        }) : ''}
        message={monthChange ? t(monthChange.isNew ? 'monthChange.createMessage' : 'monthChange.moveMessage', {
          date: formatLongDate(monthChange.date, i18n.language),
          currentMonth: formatMonthLabel(toISODate(currentYear, currentMonth, 1), i18n.language),
        }) : ''}
        confirmText={t(monthChange?.isNew ? 'monthChange.createConfirm' : 'monthChange.moveConfirm')}
        cancelText={tc('cancel')}
        onConfirm={confirmMonthChange}
        onClose={closeMonthChange}
      />

      {/* Confirmation : la pause impacte des opérations validées */}
      <ConfirmDialog
        isOpen={!!pauseConfirm}
        variant="warning"
        title={t('pause.validatedTitle')}
        message={t('pause.validatedMessage', { count: pauseConfirm?.count ?? 0 })}
        confirmText={t('pause.validatedConfirm')}
        cancelText={tc('cancel')}
        onConfirm={async () => {
          setPauseConfirm(null);
          await performSave(true);
          await fetchData();
        }}
        onClose={() => {
          setPauseConfirm(null);
          // Annulation : recharger pour écarter le changement de pause non appliqué
          fetchData();
        }}
      />

      {/* Modal de configuration de récurrence */}
      {recurrenceModalIndex !== null && rows[recurrenceModalIndex] && (
        <RecurrenceConfigModal
          isOpen={recurrenceModalIndex !== null}
          onClose={() => setRecurrenceModalIndex(null)}
          onSave={handleSaveRecurrenceConfig}
          config={rows[recurrenceModalIndex].recurrenceConfig}
          operationDate={rows[recurrenceModalIndex].operationDate || `${currentYear}-${String(currentMonth).padStart(2, '0')}-15`}
          defaultMonth={currentMonth}
          defaultYear={currentYear}
        />
      )}

      {/* Modal de configuration de répartition personnalisée */}
      {customSplitModalIndex !== null && rows[customSplitModalIndex] && (
        <CustomSplitConfigModal
          isOpen={customSplitModalIndex !== null}
          onClose={() => setCustomSplitModalIndex(null)}
          onSave={handleSaveCustomSplitConfig}
          currentUnit={rows[customSplitModalIndex].customSplitUnit}
          currentReferencePartner={rows[customSplitModalIndex].customReferencePartner}
          currentPercentage={rows[customSplitModalIndex].customPartner1Percentage}
          currentFixedAmount={rows[customSplitModalIndex].customFixedAmount}
          operationAmount={parseFloat(rows[customSplitModalIndex].amount) || 0}
          partner1Name={partner1Name}
          partner2Name={partner2Name}
        />
      )}

      {/* Modal de transfert d'opérations */}
      <TransferModal
        isOpen={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        onTransfer={handleBulkTransfer}
        sourceType="shared"
        operationCount={selectedRowIds.size}
        hasRecurring={someSelectedRecurring}
        currentAccountId={selectedAccountId}
        hasCouple={!!user?.coupleId}
        isLoading={transferLoading}
      />

      <DuplicateModal
        isOpen={duplicateModalOpen}
        sourceCount={selectedRowIds.size}
        onClose={() => setDuplicateModalOpen(false)}
        onConfirm={handleDuplicateSelection}
      />
      </>)}
    </div>
  );
}
